import { ActivatedChallengeApprovalDetails, ChallengeActivity, ChallengeDefaultTab, ChallengeLocation, ChallengeReward, ChallengeType, ConnectedAccountStatus, CreateChallengeRequest, EarnedReward, EventTargetType, FeatureType, FormFieldType, GetActivatedChallengeResponse, GetChallengeResponse, GetMessageResponse, GetOrganizationProductResponse, ImageUpload, ObjectId, OrganizationPurchaseHistory, PaymentTargetType, RegistrationSettings, RewardCriteria } from '../../../types';
import { DEFAULT_CHALLENGE_ACTIVITIES, DEFAULT_CHALLENGE_ACTIVITY_DESCRIPTION, DEFAULT_CHALLENGE_ACTIVITY_ICON, DEFAULT_CHALLENGE_ACTIVITY_TITLE, DEFAULT_CHALLENGE_REWARDS, DEFAULT_CHALLENGE_REWARD_ICON, DEFAULT_TREASURE_HUNT_REGISTRATION_SETTINGS } from '../../../helpers/constants';
import { LitElement, css, html } from 'lit';
import { convertLocaleToUTC as convertLocaleToUtc, convertUTCToLocale as convertUtcToLocale } from '../../../helpers/date';
import { customElement, query, state } from 'lit/decorators';
import { delay, isDefined, isValidString, undefinedIfEmpty } from '../../../helpers/utils';
import { duplicateChallengeToTopic, getActivatedChallengesForChallenge, getDuplicateableChallenges } from '../../../helpers/api/challenge-api';
import { isNumber, round } from '@turf/helpers';

import { CreateMessageComponent } from '../../create-message-component';
import { CreateProductComponent } from '../../create-product-component';
import { DatePicker } from '@vaadin/vaadin-date-picker';
import { Dialog } from '@material/mwc-dialog';
import { ErrorToastElement } from '../../error-toast';
import { FormSwitch } from '../../theme/form-switch';
import { ImageUploadComponent } from '../../image-upload-component';
import { RichTextEditor } from '../../common/rich-text-editor';
import { SettingDrawer } from '../../mobile/setting-drawer';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import TurndownService from 'turndown';
import accountInfo from '../../../helpers/state/account-info';
import challengeData from '../../../helpers/state/challenge-data';
import featuresInfo from '../../../helpers/state/features-info';
import format from 'date-fns/format';
import { getMainPage } from '../../../helpers/page-name';
import { getOrganizationMessages } from '../../../helpers/api/messages-api';
import { getOrganizationProducts } from '../../../helpers/api/products-api';
import { getOrganizationPurchaseHistory } from '../../../helpers/api/purchases-api';
import { loadStripeStatus } from '../../../helpers/api/stripe-api';
import { marked } from 'marked';
import { metersToMiles } from '../../../helpers/shapes';
import organizationInfo from '../../../helpers/state/organization-info';
import pointCache from '../../../helpers/state/point-cache';
import { timezoneAsync } from '../../../helpers/api/google-api';
import topicData from '../../../helpers/state/topic-data';

@customElement('admin-treasure-hunt-addon')
export class AdminTreasureHuntAddon extends LitElement {
    amplitude_callsite: string = 'Treasure Hunt Settings Drawer';

    @query('#errorToast')
    private readonly errorToastElement?: ErrorToastElement;

    @query('#assignChallengeCheckinsDrawer')
    assignChallengeCheckinsDrawer?: SettingDrawer;

    @query('#assignChallengeCheckinsModal')
    assignChallengeCheckinsModal?: Dialog;

    @query('#challengeParticipantsProgressDrawer')
    challengeParticipantsProgressDrawer?: SettingDrawer;

    @query('#challengeParticipantsProgressModal')
    challengeParticipantsProgressModal?: Dialog;

    @query('#challengeParticipantDetailsDrawer')
    challengeParticipantDetailsDrawer?: SettingDrawer;

    @query('#challengeParticipantDetailsModal')
    challengeParticipantDetailsModal?: Dialog;

    @query('#description')
    private readonly descriptionElement?: RichTextEditor | null;

    @query('#startDate')
    public startDateElement!: DatePicker;

    @query('#endDate')
    public endDateElement!: DatePicker;

    @query('#geofence-radius')
    public geofenceRadiusElement!: HTMLInputElement;

    @query('#coverImageUpload')
    public coverImageUpload!: ImageUploadComponent;

    @query('#liveSwitch')
    public liveSwitch!: FormSwitch;

    @query('#createMessageComponent')
    public createMessageComponent?: CreateMessageComponent;

    @query('#join-code')
    public joinCodeElement?: HTMLInputElement;

    @query('#createProductComponent')
    createProductComponent?: CreateProductComponent;

    @state()
    isReady: boolean = false;

    @state()
    selected_tab: string = 'Setup';

    @state()
    newStartDate?: string;

    @state()
    newstartDateUTC?: string;

    @state()
    newEndDate?: string;

    @state()
    newendDateUTC?: string;

    @state()
    challenge_name?: string;

    @state()
    can_view_challenge_after_end_date: boolean = false;

    @state()
    challenge_description?: string;

    @state()
    challenge_terms?: string;

    @state()
    challenge_terms_required?: boolean = false;

    @state()
    challenge_button_text?: string;

    @state()
    should_verify_check_ins: boolean = true;

    @state()
    enable_code_checkin: boolean = true;

    @state()
    allow_geolocation_check_in: boolean = false;

    @state()
    allow_qr_check_in: boolean = false;

    @state()
    allow_photo_check_in: boolean = false;

    @state()
    auto_approve_photo_check_in: boolean = false;

    @state()
    allow_receipt_check_in: boolean = false;

    @state()
    auto_approve_receipt_check_in: boolean = false;

    @state()
    receipt_currency: string = 'USD';

    @state()
    receipt_instructions?: string;

    @state()
    photo_instructions?: string;

    @state()
    default_geofence_radius: number | undefined;

    @state()
    should_show_special_offers: boolean = false;

    @state()
    challengeRewards: ChallengeReward[] = [];

    @state()
    showChallengeRewardDrawer: boolean = false;

    @state()
    activeChallengeReward?: ChallengeReward;

    @state()
    activeChallengeRewardIndex: number = 0;

    @state()
    challengeActivities: ChallengeActivity[] = [];

    @state()
    showChallengeActivityDrawer: boolean = false;

    @state()
    activeChallengeActivity?: ChallengeActivity;

    @state()
    activeChallengeActivityIndex: number = 0;

    @state()
    geofence_unit: string = 'meters';

    @state()
    show_leaderboard: boolean = false;

    @state()
    show_photo_feed: boolean = false;

    @state()
    default_tab: ChallengeDefaultTab = ChallengeDefaultTab.About;

    @state()
    checkin_frequency_recurrance: 'daily' | 'weekly' | 'monthly' | 'once' = 'once';

    @state()
    checkin_frequency_quantity: number = 1;

    @state()
    imageEditing?: 'cover' | 'logo';

    @state()
    challenge?: GetChallengeResponse;

    @state()
    is_registration_active: boolean = false;

    @state()
    challengeLocations: ChallengeLocation[] = [];

    @state()
    activatedChallenges: GetActivatedChallengeResponse[] = [];

    @state()
    allImageUrls: string[] = [];

    @state()
    earned_rewards: EarnedReward[] = [];

    @state()
    total_checkin_count: number = 0;

    @state()
    total_point_count: number = 0;

    @state()
    challengeStartDateLocal: string = '';

    @state()
    challengeEndDateLocal: string = '';

    @state()
    allow_internal_notes: boolean = false;

    @state()
    timezoneId: string = '';

    @state()
    needs_save: boolean = false;

    @state()
    registration_settings: RegistrationSettings = DEFAULT_TREASURE_HUNT_REGISTRATION_SETTINGS;

    @state()
    activatedChallenge?: GetActivatedChallengeResponse;

    @state()
    imagesToUpload: Array<ImageUpload | undefined> = [];

    @state()
    showPendingApprovals: boolean = false;

    @state()
    usersAreFiltered: boolean = false;

    @state()
    timezoneShortName: string = '';

    @state()
    challengeMessages: GetMessageResponse[] = [];

    @state()
    isEditingJoinCode: boolean = false;

    @state()
    challengeProducts: GetOrganizationProductResponse[] = [];

    @state()
    purchaseHistory: OrganizationPurchaseHistory | undefined;

    @state()
    stripeInfo: ConnectedAccountStatus | undefined;

    @state()
    isEditingProduct: boolean = false;

    @state()
    preCreationView: boolean = false;

    @state()
    importableChallenges: GetChallengeResponse[] = [];

    @state()
    import_challenge_id: string = '';

    options: string[] = ['Setup', 'Places', 'Activities', 'Registration & Sharing', 'Participants and Results', 'Check Ins', 'Messages', 'Payment', 'Services'];

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        // Wait for topic data to be ready.
        while (!topicData.isReady) {
            await delay(100);
        }

        // Wait for challenge data to be ready.
        const treasure_hunt_challenges = topicData.allChallenges.filter((c) => c.challenge_type === 'treasure');
        if (!isDefined(this.challenge) && treasure_hunt_challenges.length === 0 && topicData.isReady) {
            this.preCreationView = true;
            this.importableChallenges = await getDuplicateableChallenges(topicData.organizationId ?? '', ChallengeType.Treasure);
            return;
        }

        // Set key fields.
        this.challenge = topicData.allChallenges.find((c) => c.challenge_type === 'treasure');
        // VERY IMPORTANT BELOW FOR CASES WHERE USER IS SWITCHING BETWEEN APPS
        if ((this.challenge?._id.$oid !== challengeData.challenge?._id.$oid && isDefined(this.challenge)) || (isDefined(this.challenge) && pointCache.points.length > 0 && challengeData.challengeLocations.length === 0)) {
            // if we are switching or if the challenge instantiated before points were loaded, we need to re-instantiate the challenge data.
            await challengeData.instantiateChallengeData(this.challenge._id.$oid, topicData.topicKey);
        }

        if (!isDefined(this.challenge)) {
            getMainPage().errorToastElement?.showToastAndThrow('No challenge found.');
            return;
        }
        this.challenge_name = this.challenge.name ?? '';

        // Populate all of the bound fields.
        this.challenge_description = this.challenge.description ?? '';
        this.challenge_terms = this.challenge.terms ?? '';
        this.challenge_terms_required = this.challenge.registration_settings?.opt_in_required ?? false;
        this.challengeRewards = this.challenge.rewards ?? [];
        this.challengeActivities = this.challenge.activities ?? [];
        this.challengeLocations = this.challenge.locations ?? [];
        this.is_registration_active = this.challenge.is_registration_active;
        this.should_verify_check_ins = this.challenge.should_verify_check_ins ?? true;
        this.enable_code_checkin = this.challenge.enable_code_checkin ?? true;
        this.should_show_special_offers = this.challenge.should_show_special_offers ?? true;
        this.can_view_challenge_after_end_date = this.challenge.can_view_challenge_after_end_date ?? false;
        this.challenge_button_text = this.challenge.button_text ?? 'Check In';
        this.show_leaderboard = this.challenge.show_leaderboard ?? true;
        this.allow_internal_notes = this.challenge.allow_internal_notes ?? false;
        this.checkin_frequency_quantity = this.challenge.checkin_frequency_quantity ?? 1;
        this.checkin_frequency_recurrance = this.challenge.checkin_frequency_recurrance ?? 'once';
        this.show_photo_feed = this.challenge.show_photo_feed ?? false;
        this.default_tab = this.challenge.default_tab ?? ChallengeDefaultTab.About;
        this.registration_settings = this.challenge.registration_settings ?? DEFAULT_TREASURE_HUNT_REGISTRATION_SETTINGS;

        this.allow_geolocation_check_in = isDefined(this.challenge.geofence_radius_m) ?? false;
        this.default_geofence_radius = this.challenge.geofence_radius_m ?? undefined;

        this.allow_qr_check_in = this.challenge.enable_qr_checkin ?? true;
        this.allow_photo_check_in = this.challenge.enable_photo_checkin ?? true;
        this.auto_approve_photo_check_in = this.challenge.photo_auto_approve ?? false;
        this.allow_receipt_check_in = this.challenge.enable_receipt_checkin ?? false;
        this.auto_approve_receipt_check_in = this.challenge.receipt_auto_approve ?? false;
        this.receipt_currency = this.challenge.receipt_settings?.currency ?? 'USD';
        this.receipt_instructions = this.challenge.receipt_settings?.instructions;
        this.photo_instructions = this.challenge.photo_instructions;

        this.imagesToUpload = this.challengeRewards.map((c) => undefined);

        // Get the activated challenges (users that have begun participation).
        await this.getActivatedChallenges();

        // Mark self as ready to present.
        this.isReady = true;

        await this.getChallengeMessages();
        await this.getChallengeProducts();

        if (this.descriptionElement?.root) this.descriptionElement.root.innerHTML = marked.parse(this.challenge_description ?? '');

        // Kick off background task to ensure that the locales get updated.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.ensureProperDateLocales();

        // Request an animation frame to ensure that the UI is updated.
        this.requestUpdate();
    }

    async getChallengeProducts() {
        this.isEditingProduct = false;
        try {
            this.challengeProducts = await getOrganizationProducts(PaymentTargetType.ActivatedChallenge, this.challenge?._id.$oid ?? '', topicData.topic?.organization_id?.$oid ?? '', topicData.topicKey);
        } catch (error) {
            console.error('Failed to load challenge products:', error);
            getMainPage().errorToastElement.showToast('Failed to load challenge products.');
            this.challengeProducts = [];
        }
    }

    private async ensureProperDateLocales() {
        // Define a self-invoking async function to ensure that the locales are updated, and execute that function immediately, holding onto the `Promise`.
        const getTimezoneInfosTask = this.ensureTimezoneId();

        // Define a self-invoking async function to ensure that the start date element is defined, and execute that function immediately, holding onto the `Promise`.
        const ensureStartDateElementisDefinedTask = (async () => {
            while (!isDefined(this.startDateElement)) {
                await delay(100);
            }
        })();

        // Wait for both tasks to complete.
        await Promise.all([getTimezoneInfosTask, ensureStartDateElementisDefinedTask]);

        // Bail if there is no challenge so that typechecking sucks less.
        if (!isDefined(this.challenge)) {
            return;
        }

        // Populate the start and end dates with the proper timezone.
        if (isDefined(this.challenge.start_time)) {
            const startLocal = convertUtcToLocale(this.challenge.start_time, this.timezoneId);
            this.startDateElement.value = format(startLocal, 'yyyy-MM-dd');
        }
        if (isDefined(this.challenge.end_time)) {
            const endLocal = convertUtcToLocale(this.challenge.end_time , this.timezoneId);
            this.endDateElement.value = format(endLocal, 'yyyy-MM-dd');
        }

        // Update the local start and end dates.
        this.challengeStartDateLocal = this.startDateElement.value;
        this.challengeEndDateLocal = this.endDateElement.value;
    }

    private async ensureTimezoneId() {
        if (isValidString(this.timezoneId)) {
            return;
        }

        const timezoneInfos = await timezoneAsync(String(topicData.topic?.default_map_center.latLng.lat), String(topicData.topic?.default_map_center.latLng.lng));
        this.timezoneId = timezoneInfos[0]?.identifier;
        this.timezoneShortName = timezoneInfos[0]?.shortIdentifier;
    }



    async getActivatedChallenges() {
        if (this.challenge?._id) {
            this.activatedChallenges = await getActivatedChallengesForChallenge(this.amplitude_callsite, this.challenge._id.$oid, topicData.topicKey);

            const all_earned_rewards: ChallengeReward[] = [];

            this.total_checkin_count = 0;
            this.total_point_count = 0;

            this.activatedChallenges.forEach((c) => {
                all_earned_rewards.push(...c.earned_rewards);
                this.total_checkin_count += c.checkin_count;
                this.total_point_count += c.point_count;
            });

            this.earned_rewards = all_earned_rewards
            .filter((obj, index, array) => {
                return array.findIndex((item) => item._id?.$oid === obj._id?.$oid) === index;
            })
            .map((reward) => {
                const count = all_earned_rewards.filter((obj) => obj._id?.$oid === reward._id?.$oid).length;

                return {
                    title: reward.title,
                    image_url: reward.image_url,
                    count,
                    reward_id: reward._id
                };
            });

            // get all urls sorted by last updated
            this.allImageUrls = this.activatedChallenges.sort((a, b) => {
                return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
                } )
                .map((c) => c.images?.map((i) => i.image_url.startsWith('/i/') ? `https://betamap.proxi.co${i.image_url}` : i.image_url))
                .flat()
                .filter((url): url is string => url !== undefined);
        }

        const image_submission_activities = this.challengeActivities.filter((activity) => activity.submission_type === 'photos');
        const image_submission_activities_ids = image_submission_activities.map((activity) => activity._id?.$oid);

        const image_submissions_for_activities = this.activatedChallenges
            .map((c) => c.activities?.filter((activity) => image_submission_activities_ids.includes(activity.activity_id.$oid)))
            .flat()
            .filter((activity) => activity);

        const image_submissions = image_submissions_for_activities
            .map((activity) => activity?.submission)
            .flat()
            .filter((url): url is string => url !== undefined);

        this.allImageUrls.push(...image_submissions);
    }

    async handleChangeTab(e: CustomEvent) {
        this.tabIndex = e.detail.tabIndex;
        this.selected_tab = this.options[this.tabIndex];

        if (this.selected_tab === 'Payment' && organizationInfo.organization?.is_administrator === true) {
            void this.loadPaymentData();
        }

        if (this.selected_tab === 'Setup') {
            await delay(500).then(() => {
                if (this.descriptionElement?.root) this.descriptionElement.root.innerHTML = marked.parse(this.challenge_description ?? '');
            });
        }
    }

    async saveJoinCode() {
        if (!this.joinCodeElement) return;
        const code = this.joinCodeElement.value;
        if (this.challenge?._id) {
            try {
                this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {join_code: code}, this.amplitude_callsite);
                this.challenge = topicData.allChallenges.find((c) => c._id?.$oid === this.challenge?._id?.$oid);
            } catch (error) {
                getMainPage().errorToastElement.showToastAndThrow('Error saving join code, it may be invalid or already be in use.');
            }
        } else {
            this.needs_save = true;
        }
        this.isEditingJoinCode = false;
    }

    async onChangeStartDate(e: CustomEvent) {
        this.newStartDate = this.startDateElement.value;
        this.newstartDateUTC = convertLocaleToUtc(this.newStartDate, this.timezoneId)?.toISOString(); // send this with change
        if (this.challenge?._id){
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {start_time: this.newstartDateUTC}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeEndDate(e: CustomEvent) {
        this.newEndDate = `${this.endDateElement.value}T23:59:00.000Z`;
        this.newendDateUTC = convertLocaleToUtc(this.newEndDate, this.timezoneId)?.toISOString(); // send this with change
        if (this.challenge?._id){
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {end_time: this.newendDateUTC}, this.amplitude_callsite);
            this.challenge = topicData.allChallenges.find((c) => c._id?.$oid === this.challenge?._id?.$oid);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeChallengeName(e: CustomEvent) {
        this.challenge_name = (e.target as HTMLInputElement).value;
        if (this.challenge?._id){
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {name: this.challenge_name}, this.amplitude_callsite);
            this.challenge = topicData.allChallenges.find((c) => c._id?.$oid === this.challenge?._id?.$oid);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeChallengeDescription(e: CustomEvent) {
        if (this.descriptionElement?.root && this.descriptionElement.root.innerHTML.length > 6000) {
            getMainPage().errorToastElement.showToastAndThrow(
                `Description is too long ${length} chars. Please shorten the description to 6000 characters or less.`
            );
        }
        const turndownService = new TurndownService();
        const description = turndownService.turndown(this.descriptionElement?.root?.innerHTML ?? '');
        this.challenge_description = description;
        if (this.challenge?._id){
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {description}, this.amplitude_callsite);
            this.challenge = topicData.allChallenges.find((c) => c._id?.$oid === this.challenge?._id?.$oid);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeChallengeTerms(e: CustomEvent) {
        this.challenge_terms = (e.target as HTMLInputElement).value;
        if (this.challenge?._id){
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {terms: this.challenge_terms}, this.amplitude_callsite);
            this.challenge = topicData.allChallenges.find((c) => c._id?.$oid === this.challenge?._id?.$oid);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeChallengeTermsRequired(e: CustomEvent) {
        this.challenge_terms_required = (e.target as HTMLInputElement).checked;
        this.registration_settings.opt_in_required = this.challenge_terms_required;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: {...this.registration_settings, opt_in_required: this.challenge_terms_required}}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeChallengeButtonText(e: CustomEvent) {
        this.challenge_button_text = (e.target as HTMLInputElement).value;
        if (this.challenge?._id){
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {button_text: this.challenge_button_text}, this.amplitude_callsite);
            this.challenge = topicData.allChallenges.find((c) => c._id?.$oid === this.challenge?._id?.$oid);
        } else {
            this.needs_save = true;
        }
    }

    async closeDrawers() {
        await this.assignChallengeCheckinsDrawer?.close();
        await this.challengeParticipantsProgressDrawer?.close();
        await this.challengeParticipantDetailsDrawer?.close();
    }

    closeChallengeReward(){
        this.showChallengeRewardDrawer = false;
        this.activeChallengeReward = undefined;
    }

    closeChallengeActivity(){
        this.showChallengeActivityDrawer = false;
        this.activeChallengeActivity = undefined;
    }

    openChallengeReward(e: CustomEvent){
        this.showChallengeRewardDrawer = true;
        this.activeChallengeReward = e.detail.data;
        this.activeChallengeRewardIndex = e.detail.index;
    }

    openChallengeActivity(e: CustomEvent){
        this.showChallengeActivityDrawer = true;
        this.activeChallengeActivity = e.detail.data;
        this.activeChallengeActivityIndex = e.detail.index;
    }

    async getJoinCode () {
        const joinCode = this.challenge?.join_code;
        if (joinCode) {
            await navigator.clipboard.writeText(joinCode);
            getMainPage().errorToastElement.showToast('Join code copied to clipboard');
        } else {
            getMainPage().errorToastElement.showToastAndThrow('No join code found');
        }

    }

    private createNewChallengeReward() {
        const newChallengeReward: ChallengeReward = {
            _id: undefined,
            title: '',
            description: '',
            image_url: DEFAULT_CHALLENGE_REWARD_ICON,
            reward_criteria: RewardCriteria.AllPoints,
            reward_criteria_threshold: 0,
            completion_message: undefined,
            selected_category_or_tag_id: undefined,
            selected_location_id: undefined,
            redeemable: false,
            completion_url: undefined,
        };
        this.challengeRewards.push(newChallengeReward);
        this.activeChallengeRewardIndex = this.challengeRewards.length - 1;
        this.activeChallengeReward = this.challengeRewards[this.activeChallengeRewardIndex];
        this.showChallengeRewardDrawer = true;
    }

    private createNewChallengeActivity() {
        const newChallengeActivity: ChallengeActivity = {
            _id: undefined,
            name: DEFAULT_CHALLENGE_ACTIVITY_TITLE,
            description: DEFAULT_CHALLENGE_ACTIVITY_DESCRIPTION,
            image_url: DEFAULT_CHALLENGE_ACTIVITY_ICON,
            point_value: 1,
            max_redemptions: 1,
            submission_type: 'photos',
        };
        this.challengeActivities.push(newChallengeActivity);
        this.activeChallengeActivityIndex = this.challengeActivities.length - 1;
        this.activeChallengeActivity = this.challengeActivities[this.activeChallengeActivityIndex];
        this.showChallengeActivityDrawer = true;
    }


    async updateReorderedRewardItems(e: CustomEvent) {
        const reorderedData = e.detail?.data as ChallengeReward[] ?? [];
        const oldIndex = e.detail?.oldIndex;
        const newIndex = e.detail?.newIndex;
        this.challengeRewards = reorderedData;
        if (this.activeChallengeRewardIndex === oldIndex)
            this.activeChallengeRewardIndex = newIndex;

        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {rewards: this.challengeRewards}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async updateReorderedActivitysItems(e: CustomEvent) {
        const reorderedData = e.detail?.data as ChallengeActivity[] ?? [];
        const oldIndex = e.detail?.oldIndex;
        const newIndex = e.detail?.newIndex;
        this.challengeActivities = reorderedData;
        if (this.activeChallengeActivityIndex === oldIndex)
            this.activeChallengeActivityIndex = newIndex;

        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {activities: this.challengeActivities}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async saveChallengeRewardsChanges(e: CustomEvent) {
        const newData = e.detail?.newData as ChallengeReward[] ?? [];
        const imagesToUpload = e.detail?.imagesToUpload ?? [];
        this.challengeRewards = newData;
        this.imagesToUpload = imagesToUpload;
        if (this.challenge?._id) {
            await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {rewards: this.challengeRewards}, this.amplitude_callsite);
            this.challenge = topicData.allChallenges.find((c) => c._id?.$oid === this.challenge?._id?.$oid);
        } else {
            this.needs_save = true;
        }
    }

    async saveChallengeActivityChanges(e: CustomEvent) {
        const newData = e.detail?.newData as ChallengeActivity[] ?? [];
        const imagesToUpload = e.detail?.imagesToUpload ?? [];
        this.challengeActivities = newData;
        this.imagesToUpload = imagesToUpload;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {activities: this.challengeActivities}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async saveChallengeLocations(e: CustomEvent) {
        this.challengeLocations = e.detail.data ?? [];
        this.needs_save = true;

        if (this.challenge?._id) {
            this.challenge  = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {locations: this.challengeLocations}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
        this.requestUpdate();
    }

    private async loadPaymentData() {
        if (!topicData.topic?.organization_id || !isDefined(organizationInfo.organization?._id)) {
            console.error('No organization ID found for topic.');
            return;
        }
        try {
            [this.stripeInfo, this.purchaseHistory] = await Promise.all([
                loadStripeStatus(organizationInfo.organization?._id.$oid ?? ''),
                getOrganizationPurchaseHistory(organizationInfo.organization?._id.$oid ?? '', this.challenge?._id?.$oid ?? '', topicData.topicKey)
            ]);
        } catch (error) {
            getMainPage().errorToastElement.showToast('Failed to load purchases information.');
        }
    }

    async allowInternalNotesChange(e: CustomEvent) {
        this.allow_internal_notes = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {allow_internal_notes: this.allow_internal_notes}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    private downloadParticipants() {
        let content = '';

        content +=
            'Id,"Challenge Id",Name,Email,"Checkin Count","Counted Checkins","Point Count","Created At","Updated At"\n';

        for (const activatedChallenge of this.activatedChallenges) {
            content += `${activatedChallenge._id.$oid},${activatedChallenge.challenge_id.$oid},"${activatedChallenge?.display_name ?? ''}","${
                activatedChallenge?.email ?? ''
            }",${activatedChallenge.checkin_count},${activatedChallenge.counted_checkins?.length ?? 0},${activatedChallenge.point_count},"${activatedChallenge.created_at ?? ''}","${
                activatedChallenge.updated_at ?? ''
            }"\n`;
        }

        const filename = 'activated_challenges.csv';

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    downloadImage(imageUrl: string) {
        const a = document.createElement('a');
        a.href = imageUrl;
        a.download = imageUrl;
        a.click();
    }

    async getChallengeMessages() {
        try {
            this.challengeMessages = await getOrganizationMessages(EventTargetType.ActivatedChallenge, this.challenge?._id.$oid ?? '', topicData.topic?.organization_id?.$oid ?? '', topicData.topicKey);
        } catch (error) {
            console.error('Failed to load challenge messages:', error);
            getMainPage().errorToastElement.showToast('Failed to load challenge messages.');
            this.challengeMessages = [];
        }
    }


    openImageInNewTab(imageUrl: string) {
        window.open(imageUrl, '_blank');
    }

    async setEditedMessage(e: CustomEvent) {
        const message = e.detail.message;
        if (this.createMessageComponent) {
            await this.createMessageComponent.setExistingMessage(message);
        }
    }

    async setEditedProduct(e: CustomEvent) {
        this.isEditingProduct = true;
        this.requestUpdate();
        await delay(100);
        const product = e.detail.product;
        if (this.createProductComponent) {
            await this.createProductComponent.setExistingProduct(product);
        }
    }

    renderProductCenter() {
        if (!(organizationInfo.organization?.is_administrator ?? false)) {
            return html`
                <settings-card header="Payment Settings">
                    <p class="info-paragraph">You do not have the required permissions to view or manage products and purchases. Please contact your organization administrator for access.</p>
                </settings-card>
            `;
        }

        return html`
            <settings-card header="Create Products">
                <p class="info-paragraph">Create and manage charges that can be added to the registration process for this challenge.</p>
                    <create-product-component
                    id="createProductComponent"
                        .payment_target_type=${PaymentTargetType.ActivatedChallenge}
                        .stripeInfo=${this.stripeInfo}
                        .payment_target_entity_id=${this.challenge?._id.$oid ?? ''}
                        @product-updated="${this.getChallengeProducts}"
                    >
                    </create-product-component>
            </settings-card>
            <settings-card header="Existing Products and Prices">
                <p class="info-paragraph">View and manage existing products and prices</p>
                ${this.challengeProducts.length > 0 ? html`
                    <existing-products-component
                        .data="${this.challengeProducts}"
                        .payment_target_type=${PaymentTargetType.ActivatedChallenge}
                        .purchases=${this.purchaseHistory?.transactions ?? []}
                        @set-edited-product="${this.setEditedProduct}"
                        @update-products="${this.getChallengeProducts}"
                    >
                    </existing-products-component>
                ` : html`
                    <p>No products found.</p>
                `}
            </settings-card>
            <settings-card header="Completed Purchases">
                <p class="info-paragraph">View all completed purchases made by users.</p>
                ${!isDefined(this.purchaseHistory) || this.purchaseHistory.transactions.length > 0 ? html`
                    <completed-purchases-component
                        .data=${this.purchaseHistory}
                    ></completed-purchases-component>
                ` : html`
                    <p>No completed purchases found.</p>
                `}
            </settings-card>
        `;
    }

    private downloadCheckins() {
        let content = '';

        content +=
            'Id,"Location Name","Timestamp","Participant Id","Participant Name","Participant Email"\n';


        // for all activated challenge, iterate through each checkin_location and add to content
        for (const activatedChallenge of this.activatedChallenges) {
            if (activatedChallenge.checkin_locations) {
                const sortedCheckinLocations = [...activatedChallenge.checkin_locations].sort((a, b) =>
                    a.search.localeCompare(b.search)
                );

                for (const checkinLocation of sortedCheckinLocations) {
                    content += `${activatedChallenge._id.$oid},"${checkinLocation.search}","${
                        activatedChallenge.created_at
                    }","${activatedChallenge.display_name ?? ''}","${
                        activatedChallenge?.display_name ?? ''
                    }","${activatedChallenge?.email ?? ''}"\n`;
                }
            }
        }
        const filename = 'checkins.csv';

        const blob = new Blob([content], {
            type: 'text/plain;charset=utf-8',
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    get pendingPoints() {
        const activatedChallengesWithPendingCheckins = this.activatedChallenges.filter(
            (challenge) => challenge.pending_checkin_location_ids && challenge.pending_checkin_location_ids.length > 0
        );

        const activatedChallengesWithPendingActivities = this.activatedChallenges.filter(
            (challenge) => isDefined(challenge.activities) && challenge.activities.filter((activity) => !activity.approved).length > 0
        );

        const allPendingActivatedChallenges = [
            ...activatedChallengesWithPendingCheckins,
            ...activatedChallengesWithPendingActivities
        ];

        // Use a Map to ensure unique challenges based on their id
        const uniquePendingActivatedChallengesMap = new Map();
        allPendingActivatedChallenges.forEach(challenge => {
            uniquePendingActivatedChallengesMap.set(challenge._id.$oid, challenge);
        });

        const uniquePendingActivatedChallenges: GetActivatedChallengeResponse[] = Array.from(uniquePendingActivatedChallengesMap.values());

        // create array with all pending checkin locations, and activated_challenge id
        // TOOD Add activities and names of places
        const pendingCheckins = uniquePendingActivatedChallenges.map((challenge) => {
            const checkins: ActivatedChallengeApprovalDetails[] = [];

            challenge.pending_checkin_location_ids?.forEach((location_activity_id) => {
                const challengeLocation = this.challengeLocations.find((location) => location._id?.$oid === location_activity_id) ?? undefined;
                const imageUrl = (challenge.pending_images ?? challenge.images)?.find((image) => image.challenge_location_id.$oid === location_activity_id)?.image_url ??
                    challenge.receipts?.find((receipt) => receipt.challenge_location_id.$oid === location_activity_id)?.image_url ?? '';
                if (!challengeLocation) return;
                    const points = pointCache.getPointsFromLocation(challengeLocation.location);
                    checkins.push({
                        type: 'location',
                        location_activity_id,
                        activated_challenge_id: challenge._id.$oid,
                        submission: ['Image', [imageUrl]],
                        location_activity_name: points?.map((point) => point.name).join(', ') ?? '',
                        email: challenge.email,
                        username: challenge.display_name,
                        phone_number: challenge.phone_number,
                        activity_submission_type: undefined,
                    });
            });

            challenge.activities?.forEach((activity) => {
                if (!activity.approved) {
                    const type = this.challengeActivities.find((a) => a._id?.$oid === activity.activity_id.$oid)?.submission_type;
                    checkins.push({
                        type: 'activity',
                        location_activity_id: activity.activity_id.$oid,
                        activated_challenge_id: challenge._id.$oid,
                        submission: [type ?? 'Submission', activity.submission],
                        location_activity_name: this.challengeActivities.find((a) => a._id?.$oid === activity.activity_id.$oid)?.name ?? '',
                        email: challenge.email,
                        username: challenge.display_name,
                        phone_number: challenge.phone_number,
                        activity_submission_type: type
                    });
                }});

            return checkins.filter((checkin) => checkin) ?? [] as ActivatedChallengeApprovalDetails[];
        });

        // remove undefined values
        pendingCheckins.forEach((checkins) => {
            checkins?.filter((checkin) => checkin);
        } );

        // flatten array and put into ActivatedCHallengeRequestDetails format
        const pendingCheckinsFlattened: ActivatedChallengeApprovalDetails[] = [];
        pendingCheckins.forEach((checkins) => {
            checkins?.forEach((checkin) => {
                pendingCheckinsFlattened.push({
                    type: checkin.type,
                    activated_challenge_id: checkin.activated_challenge_id,
                    location_activity_id: checkin.location_activity_id,
                    location_activity_name: checkin.location_activity_name ?? '',
                    submission: checkin.submission,
                    phone_number: checkin.phone_number,
                    email: checkin.email,
                    username: checkin.username,
                    activity_submission_type: checkin.activity_submission_type,
                });
            });
        });

        return pendingCheckinsFlattened;
    }

    async createNewChallenge() {
        // Ensure we have the timezone.
        await this.ensureTimezoneId();

        // start one week from now
        const start = new Date();
        start.setDate(start.getDate() + 7);

        let start_str = format(start, 'yyyy-MM-dd');
        start_str = convertLocaleToUtc(start_str, this.timezoneId)?.toISOString() ?? '';

        const end = new Date();
        end.setDate(end.getDate() + 28);
        let end_str = format(end, 'yyyy-MM-dd');
        end_str = convertLocaleToUtc(end_str, this.timezoneId)?.toISOString() ?? '';

        const createChallengeRequest: CreateChallengeRequest = {
            is_live: false,
            is_registration_active: false,
            challenge_type: ChallengeType.Treasure,
            name: `The ${topicData.topic?.name ?? ''} Challenge`,
            description: `Visit all the places in ${topicData.topic?.name ?? ''} to complete this challenge!`,
            terms: '',
            start_time: start_str,
            end_time: end_str,
            locations: [],
            rewards: DEFAULT_CHALLENGE_REWARDS,
            button_text: this.challenge_button_text ?? 'Check In',
            should_verify_check_ins: this.should_verify_check_ins,
            should_verify_with_code: true,
            enable_code_checkin: this.enable_code_checkin,
            should_show_special_offers: this.should_show_special_offers,
            can_view_challenge_after_end_date: true,
            show_leaderboard: true,
            checkin_frequency_quantity: this.checkin_frequency_quantity,
            checkin_frequency_recurrance: this.checkin_frequency_recurrance,
            show_photo_feed: false,
            default_tab: this.default_tab,
            wallet_card_id: undefined,
            geofence_radius_m: 100,
            enable_qr_checkin: true,
            enable_photo_checkin: true,
            photo_auto_approve: false,
            enable_receipt_checkin: false,
            receipt_auto_approve: false,
            photo_instructions: undefined,
            allow_internal_notes: false,
            activities: DEFAULT_CHALLENGE_ACTIVITIES,
            registration_settings: DEFAULT_TREASURE_HUNT_REGISTRATION_SETTINGS,
            receipt_settings: {
                currency: 'USD',
                instructions: undefined
            },
            scavenger_hunt_settings: undefined,
            trivia_settings: undefined,
            should_request_user_images: true,
            join_code: undefined
        };

        this.challenge = await topicData.fetchAndAddChallenge(createChallengeRequest, this.amplitude_callsite);

        this.needs_save = false;

        // Refresh the data.
        await topicData.getNewChallenges();
        this.preCreationView = false;
        await this.startup();
    };

    async saveParticipantsProgress() {
        await this.getActivatedChallenges();
        if (this.activatedChallenge) {
            this.activatedChallenge = this.activatedChallenges.find((c) => c._id.$oid === this.activatedChallenge?._id.$oid);
        }
        this.requestUpdate();
    };

    async viewParticipantsProgressDetails(e: CustomEvent) {
        this.activatedChallenge = e.detail.data;
    };

    async viewPendingCheckinList() {
        this.activatedChallenge = undefined;
        this.showPendingApprovals = true;
    }

    async handleCloseParticipantsProgressDetailsModal() {
        this.activatedChallenge = undefined;
    };

    async saveParticipantDetail() {
        await this.handleCloseParticipantsProgressDetailsModal();
    };

    handleLearnMoreClick() {
        // TODO: Update this URL when Treasure Hunt documentation is available
        window.open('https://www.proxi.co/treasure-hunt-challenge', '_blank');
    }

    openUpgradeDialog(e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.ChallengePassport,
                },
            })
        );
    }

    async showLeaderboardChange(e: CustomEvent) {
        const showLeaderboard = e.detail.checked;
        this.show_leaderboard = showLeaderboard;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {show_leaderboard: showLeaderboard}, this.amplitude_callsite);
        } else {
            this.show_leaderboard = !this.show_leaderboard;
            this.needs_save = true;
        }
    }

    async changeMultipleCheckins(e: CustomEvent) {
        const multipleCheckins = e.detail.checked;
        if (multipleCheckins === true) {
            this.checkin_frequency_recurrance = 'monthly';
            if (this.challenge?._id) {
                this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid,
                    {checkin_frequency_recurrance: 'monthly'}, this.amplitude_callsite);
            } else {
                this.needs_save = true;
            }
        } else {
            this.checkin_frequency_quantity = 1;
            this.checkin_frequency_recurrance = 'once';
            if (this.challenge?._id) {
                this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid,
                    {checkin_frequency_quantity: 1, checkin_frequency_recurrance: 'once'}, this.amplitude_callsite);
            } else {
                this.needs_save = true;
            }
        }
    }


    async showCheckinFrequencyChange(e: CustomEvent) {
        const checkinFrequency = (e.target as HTMLInputElement).value ?? 'once';

        // check that it is one of the options
        if (!['daily', 'weekly', 'monthly', 'once'].includes(checkinFrequency)) {
            getMainPage().errorToastElement?.showToast('Check-in frequency must be daily, weekly, monthly or once');
            return;
        }

        // Type assertion to make TypeScript understand this is a valid value
        const typedFrequency = checkinFrequency as 'daily' | 'weekly' | 'monthly' | 'once';

        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(
                this.challenge?._id?.$oid,
                {checkin_frequency_recurrance: typedFrequency},
                this.amplitude_callsite
            );
            this.checkin_frequency_recurrance = typedFrequency;
        } else {
            this.checkin_frequency_recurrance = typedFrequency;
            this.needs_save = true;
        }

        this.checkin_frequency_recurrance = typedFrequency;
    }

    async showCheckinFrequencyQuantityChange(e: CustomEvent) {
        const checkinFrequencyQuantity = (e.target as HTMLInputElement).value ?? 1;
        // check that number is an integer
        if (!isNumber(checkinFrequencyQuantity)) {
            getMainPage().errorToastElement?.showToast('Check-in frequency quantity must be an integer');
            return;
        }
        const checkinFrequencyQuantityNum = parseInt(checkinFrequencyQuantity, 10);
         // check that number greater than 0
        if (checkinFrequencyQuantityNum < 1) {
            getMainPage().errorToastElement?.showToast('Check-in frequency quantity must be greater than 0');
            return;
        }
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {checkin_frequency_quantity: checkinFrequencyQuantityNum}, this.amplitude_callsite);
            this.checkin_frequency_quantity = checkinFrequencyQuantityNum;
        } else {
            this.checkin_frequency_quantity = checkinFrequencyQuantity ? 1 : 0;
            this.needs_save = true;
        }

        this.checkin_frequency_quantity = checkinFrequencyQuantityNum;
    }


    async showPhotoFeedChange(e: CustomEvent) {
        this.show_photo_feed = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {show_photo_feed: this.show_photo_feed}, this.amplitude_callsite);
        } else {
            this.show_photo_feed = !this.show_photo_feed;
            this.needs_save = true;
        }
    }

    async changeDefaultTab(e: CustomEvent) {
        const defaultTab = ((e.target as HTMLInputElement).value ?? ChallengeDefaultTab.About) as ChallengeDefaultTab;
        this.default_tab = defaultTab;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {default_tab: defaultTab}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async showAfterEndDateChange(e: CustomEvent) {
        this.can_view_challenge_after_end_date = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {can_view_challenge_after_end_date: this.can_view_challenge_after_end_date}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async shouldVerifyCheckInsChange(e: CustomEvent) {
        this.should_verify_check_ins = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {should_verify_check_ins: this.should_verify_check_ins}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async canCheckInWithCodeChange(e: CustomEvent) {
        this.enable_code_checkin = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {enable_code_checkin: this.enable_code_checkin}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }


    async handleLiveChange(e: CustomEvent) {
        const live = e.detail;
        const start_date_in_future =  new Date(challengeData.challenge?.start_time ?? new Date()) > new Date();
        // if the start date is in the future, show a warning
        if (live === true && start_date_in_future && this.challenge) {
            // set start date to today
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {start_time: new Date().toISOString()}, this.amplitude_callsite);
            this.startDateElement.value = format(new Date(), 'yyyy-MM-dd');
            this.challengeStartDateLocal = this.startDateElement.value;
            getMainPage().errorToastElement?.showToast('Challenge start date was in the future, so it has been set to today.');
        }

        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {is_live: live}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async handleRegistrationActiveChange(e: CustomEvent) {
        this.is_registration_active = e.detail;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {is_registration_active: this.is_registration_active}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async allowGeolocationCheckInChange(e: CustomEvent) {
        this.allow_geolocation_check_in = e.detail.checked;

        if (this.allow_geolocation_check_in) {
            this.default_geofence_radius = this.default_geofence_radius ?? 100;
        } else {
            this.default_geofence_radius = undefined;
        }
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {geofence_radius_m: this.default_geofence_radius}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async allowQRCheckInChange(e: CustomEvent) {
        this.allow_qr_check_in = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {enable_qr_checkin: this.allow_qr_check_in}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async allowPhotoCheckInChange(e: CustomEvent) {
        this.allow_photo_check_in = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {enable_photo_checkin: this.allow_photo_check_in}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async changePhotoAutoApprove(e: CustomEvent) {
        this.auto_approve_photo_check_in = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {photo_auto_approve: this.auto_approve_photo_check_in}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async allowReceiptCheckInChange(e: CustomEvent) {
        this.allow_receipt_check_in = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {enable_receipt_checkin: this.allow_receipt_check_in}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async changeReceiptAutoApprove(e: CustomEvent) {
        this.auto_approve_receipt_check_in = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {receipt_auto_approve: this.auto_approve_receipt_check_in}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async changeReceiptCurrency(e: CustomEvent) {
        this.receipt_currency = (e.target as HTMLSelectElement).value;
        const receipt_settings = {
            currency: this.receipt_currency,
            instructions: this.receipt_instructions
        };
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {receipt_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async changeReceiptInstructions(e: CustomEvent) {
        this.receipt_instructions = (e.target as HTMLInputElement).value;
        if (undefinedIfEmpty(this.receipt_instructions) === undefined) {
            this.receipt_instructions = undefined;
        }
        const receipt_settings = {
            currency: this.receipt_currency,
            instructions: this.receipt_instructions
        };
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {receipt_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async changePhotoInstructions(e: CustomEvent) {
        this.photo_instructions = (e.target as HTMLInputElement).value;
        if (undefinedIfEmpty(this.photo_instructions) === undefined) {
            this.photo_instructions = undefined;
        }
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {photo_instructions: this.photo_instructions}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async changeDefaultGeofenceRadius(e: CustomEvent) {
        const value = (e.target as HTMLInputElement).value;
        const value_number = Number(value);
        if (!isNumber(value_number) || value === '') return;
        const meters = this.translateRadiusToMeters(value_number, this.geofence_unit);
        this.default_geofence_radius = round(meters, 0);
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {geofence_radius_m: this.default_geofence_radius}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }


    async shouldShowSpecialOffersChange(e: CustomEvent) {
        this.should_show_special_offers = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {should_show_special_offers: this.should_show_special_offers}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }


    async onChangeRegistrationHeading(e: CustomEvent) {
        const value = (e.target as HTMLInputElement).value;
        this.registration_settings.heading = value;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: {...this.registration_settings, heading: value}}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeRegistrationDescription(e: CustomEvent) {
        const value = (e.target as HTMLInputElement).value;
        this.registration_settings.description = value;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: {...this.registration_settings, description: value}}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeRegistrationButtonText(e: CustomEvent) {
        const value = (e.target as HTMLInputElement).value;
        this.registration_settings.button_label = value;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: {...this.registration_settings, button_label: value}}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async onChangeRegistrationCoverImage(e: CustomEvent) {
        const value = e.detail?.imageUrl ?? '';
        this.registration_settings.cover_image_url = value;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: {...this.registration_settings, cover_image_url: value}}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }

        this.registration_settings.cover_image_url = value;
        this.imageEditing = undefined;
        this.requestUpdate();
    }


    async onChangeRegistrationLogo(e: CustomEvent) {
        const value = e.detail?.imageUrl ?? '';
        this.registration_settings.logo_url = value;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: {...this.registration_settings, logo_url: value}}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }

        this.registration_settings.logo_url = value;
        this.imageEditing = undefined;
        this.requestUpdate();
    }

    async onChangeRegistrationLogoBackground(e: CustomEvent) {
        this.registration_settings.show_logo_background = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: {...this.registration_settings}}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
        this.requestUpdate();
    }

    async requireEmailChange(e: CustomEvent) {
        this.registration_settings.email_required = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: this.registration_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async showEmailChange(e: CustomEvent) {
        this.registration_settings.show_email = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: this.registration_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async requirePhoneChange(e: CustomEvent) {
        this.registration_settings.phone_required = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: this.registration_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async showPhoneChange(e: CustomEvent) {
        this.registration_settings.show_phone = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: this.registration_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async removeAdditionalField(index: number) {
        if (!this.registration_settings.additional_fields) {
            this.registration_settings.additional_fields = [];
        }
        this.registration_settings.additional_fields.splice(index, 1);
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: this.registration_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async changeAdditionalFields(index: number, field_name: string, e: CustomEvent) {
        if (!this.registration_settings.additional_fields) {
            this.registration_settings.additional_fields = [];
        }
        switch (field_name) {
            case 'label': {
                const value = (e.target as HTMLInputElement).value;
                this.registration_settings.additional_fields[index].label = value;
                break;
            }
            case 'field_type': {
                const value = (e.target as HTMLSelectElement).value as FormFieldType;
                this.registration_settings.additional_fields[index].field_type = value;
                break;
            }
            case 'required': {
                const value = (e.detail as HTMLInputElement).checked;
                this.registration_settings.additional_fields[index].required = value ?? false;
                break;
            }
            case 'options':
                {
                const value = (e.target as HTMLInputElement).value;
                this.registration_settings.additional_fields[index].options = value.split(',');
                break;
            }
        }

        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: this.registration_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async addAdditionalField() {
        if (!this.registration_settings.additional_fields) {
            this.registration_settings.additional_fields = [];
        }
        this.registration_settings.additional_fields.push({
            label: 'New Field',
            field_type: FormFieldType.Text,
            required: false,
            options: [],
        });

        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: this.registration_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    async filterUsersbyReward(reward_id?: ObjectId) {
        if (!reward_id) {
            return;
        }
        const award = this.challengeRewards.find((r) => r._id?.$oid === reward_id.$oid);
        this.activatedChallenges = this.activatedChallenges?.filter((c) => c.earned_rewards.find((r) => r._id?.$oid === award?._id?.$oid));
        getMainPage().errorToastElement.showToast(`Filtering by ${award?.title ?? 'reward'} Awardees`);
        this.usersAreFiltered = true;

    }

    async clearFilterUsers() {
        await this.getActivatedChallenges();
        this.usersAreFiltered = false;
    }

    async requireUsernameChange(e: CustomEvent) {
        this.registration_settings.username_required = e.detail.checked;
        if (this.challenge?._id) {
            this.challenge = await topicData.fetchAndUpdateChallenge(this.challenge?._id?.$oid, {registration_settings: this.registration_settings}, this.amplitude_callsite);
        } else {
            this.needs_save = true;
        }
    }

    translateRadiusFromMeters(meters: number, unit: string) {
        if (unit === 'meters') {
            return meters;
        } else if (unit === 'miles') {
            return metersToMiles(meters);
        } else if (unit === 'kilometers') {
            return meters / 1000;
        } else if (unit === 'feet') {
            return meters * 3.28084;
        } else {
            return meters;
        }
    }

    translateRadiusToMeters(value: number, unit: string) {
        if (unit === 'meters') {
            return value;
        } else if (unit === 'miles') {
            return value * 1609.34;
        } else if (unit === 'kilometers') {
            return value * 1000;
        } else if (unit === 'feet') {
            return value / 3.28084;
        } else {
            return value;
        }
    }

    render() {
        if (this.preCreationView) {
            return this.renderPreCreationView();
        }
        if (!topicData.isReady || !pointCache.isReady || !challengeData.isReady || !this.challenge || !this.isReady) {
            return html`<loading-element loadingString="Getting Challenge Data..."></loading-element>`;
        }
        return html`
            <div class="wrapper">
                <!-- <div class="header-title-and-buttons">
                    <h4>Challenge Settings</h4>
                </div> -->
                <div class="header-title-and-buttons" style="background-color: ${topicData.backgroundColor}; color: ${topicData.textColor}">
                    <div class="left-header-content">
                        <span class="additional chackboxHolder" style="margin-left: 0.2rem;">
                        <form-switch
                            id="liveSwitch"
                            name="liveSwitch"
                            .colored=${true}
                            style="flex: unset;"
                            ?checked=${this.challenge?.is_live}
                            ?disabled=${!featuresInfo.challengePassportEnabled}
                            @change="${this.handleLiveChange}"></form-switch>
                            </span>Treasure Hunt is Live
                        <span class="additional chackboxHolder" style="margin-left: 0.2rem;">
                        <form-switch
                            id="registrationSwitch"
                            name="liveSwitch"
                            .colored=${true}
                            ?disabled=${!featuresInfo.challengePassportEnabled}
                            style="flex: unset;"
                            ?checked=${this.challenge?.is_registration_active}
                            @change="${this.handleRegistrationActiveChange}"></form-switch>Registration is Active
                            </span>
                    </div>
                    <div class="middle-header-content">
                        ${this.challenge?.name}
                    </div>

                    <div class="right-header-content">
                        ${isDefined(this.challenge?.join_code) ? html`
                            <span style="text-align: right">
                                Users can join by going to ${window.location.hostname.toLowerCase() === 'betamap.proxi.co' ? 'betaplay.proxi.co' : 'play.proxi.co'} and entering your join code or with any of the links in the 'Registration & Sharing' tab.
                            </span>
                            <copy-link-component
                                style="color: var(--main-primary-color);"
                                text="${this.challenge?.join_code}"
                                small="${true}"
                                successMessage=${`Join code copied to clipboard`}
                                textType="text">
                                Copy Join Code
                            </copy-link-component>
                        ` : ''}
                    </div>
                </div>
                <div class="tabs">
                    <tabs-component
                        .options=${this.options}
                        .tabIndex=${this.selected_tab === 'Setup' ? 0 :
                            this.selected_tab === 'Places' ? 1 :
                            this.selected_tab === 'Activities' ? 2 :
                            this.selected_tab === 'Registration & Sharing' ? 3 :
                            this.selected_tab === 'Participants and Results' ? 4 :
                            this.selected_tab === 'Check Ins' ? 5 :
                            this.selected_tab === 'Messages' ? 6 :
                            this.selected_tab === 'Payment' ? 7 :
                            this.selected_tab === 'Services' ? 8 : 0}
                        @on-select-tab=${this.handleChangeTab}
                    ></tabs-component>
                </div>
                <div class="tab__content">
                    ${this.selected_tab === 'Setup' ? this.renderBasicDetails() : ''}
                    ${this.selected_tab === 'Places' ? this.renderParticipatingPlaces() : ''}
                    ${this.selected_tab === 'Activities' ? this.renderActivities() : ''}
                    ${this.selected_tab === 'Registration & Sharing' ? this.renderRegistrationOptions() : ''}
                    ${this.selected_tab === 'Participants and Results' ? this.renderManageParticipantsAndResults() : ''}
                    ${this.selected_tab === 'Check Ins' ? this.renderCheckIns() : ''}
                    ${this.selected_tab === 'Messages' ? this.renderMessageCenter() : ''}
                    ${this.selected_tab === 'Services' ? this.renderServices() : ''}
                    ${this.selected_tab === 'Payment' ? this.renderProductCenter() : ''}
                </div>
            </div>
        `;
    }

    renderBasicDetails() {
        return html`
        <settings-card header="Setup">
            <proxi-textfield
                ?disabled="${!featuresInfo.challengePassportEnabled}"
                outlined
                required
                class="input__challenge-title"
                id="challenge-title"
                type="text"
                label="Challenge or Treasure Hunt Name"
                placeholder="Title (e.g. Passport, Ale Trail, Treasure Hunt)"
                value="${this.challenge_name ?? ''}"
                @change="${this.onChangeChallengeName}"
            >
            </proxi-textfield>
            <rich-text-editor
                id="description"
                style="margin-top: 0.25rem;"
                showSave=true
                @save=${this.onChangeChallengeDescription}>
            </rich-text-editor>
            <h4 style="margin-bottom: 0.25rem;">
                Check In Dates
            </h4>
            <p class="subtext">
                Set the start and end dates for your challenge. Participants will only be able to earn points for check-ins during this time.
            </p>
            <div class="date-container">
                <vaadin-date-picker ?disabled="${!featuresInfo.challengePassportEnabled}"
                    required
                    id="startDate"
                    class="input__date"
                    placeholder="Start Date"
                    value="${this.newStartDate ? this.newStartDate : this.challengeStartDateLocal ?? ''}"
                    @change="${this.onChangeStartDate}"
                ></vaadin-date-picker>
                <vaadin-date-picker ?disabled="${!featuresInfo.challengePassportEnabled}"
                    required
                    id="endDate"
                    class="input__date"
                    placeholder="End Date"
                    value="${this.newEndDate ? this.newEndDate.slice(0,10) : this.challengeEndDateLocal ?? ''}"
                    @change="${this.onChangeEndDate}"
                ></vaadin-date-picker>
            </div>
            <proxi-textfield
                outlined ?disabled="${!featuresInfo.challengePassportEnabled}"
                required
                class="input__challenge-title"
                id="checkin-code"
                type="text"
                label="Name of a 'Check In' or Visit to a Location"
                placeholder="Stamp, Check In, Mark as Visited, etc"
                value="${this.challenge_button_text ?? ''}"
                @change="${this.onChangeChallengeButtonText}"
            >
            </proxi-textfield>
            </proxi-textfield>
                <proxi-selectfield ?disabled="${!featuresInfo.challengePassportEnabled}"
                    outlined
                    class="input__challenge-title"
                    id="default-tab"
                    label="Default Participant Tab"
                    .value="${this.default_tab}"
                    @change="${this.changeDefaultTab}"
                >
                    <mwc-list-item value="about">About</mwc-list-item>
                    <mwc-list-item value="checkins">Locations</mwc-list-item>
                    <mwc-list-item value="activities">Activities</mwc-list-item>
                    <mwc-list-item value="rewards">Progress</mwc-list-item>
                    <mwc-list-item value="leaderboard">Leaderboard</mwc-list-item>
                    <mwc-list-item value="photo_feed">Photo Feed</mwc-list-item>
                    <mwc-list-item value="share">Share</mwc-list-item>
                </proxi-selectfield>
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="showLeaderboard"
                        name="showLeaderboard"
                        class_name="blue"
                        ?checked="${this.show_leaderboard}"
                        @change="${this.showLeaderboardChange}"
                        >
                    </form-checkbox>
                    <span>Show Leaderboard to Participants</span>
                </div>
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="showPhotoFeed"
                        name="showPhotoFeed"
                        class_name="blue"
                        ?checked="${this.show_photo_feed}"
                        @change="${this.showPhotoFeedChange}"
                        >
                    </form-checkbox>
                    <span>Show Photo Feed to Participants (beta) </span>
                </div>
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="allowUserEndDate"
                        name="allowUserEndDate"
                        class_name="blue"
                        ?checked="${this.can_view_challenge_after_end_date}"
                        @change="${this.showAfterEndDateChange}"
                    >
                    </form-checkbox>
                    <span>Allow users to see their progress after end date? They will not be able to earn more points.</span>
                </div>
            </div>
            <div class="checkbox-wrapper">
                <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                    id="allowInternalNotes"
                    name="allowInternalNotes"
                    class_name="blue"
                    ?checked="${this.allow_internal_notes}"
                    @change="${this.allowInternalNotesChange}"
                    >
                </form-checkbox>
                <span>Allow participants to add internal notes on points (notes are private to each participant)</span>
            </div>
        </settings-card>

        <settings-card header="Check In Validation Options">
            <div class="header__actions" slot="header" style="display: flex; align-items: center;">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="requireVerification"
                        name="requireVerification"
                        class_name="blue"
                        ?checked="${this.should_verify_check_ins}"
                        @change="${this.shouldVerifyCheckInsChange}"
                        >
                    </form-checkbox>
                    <span style="display:flex; align-items: center; font-weight: normal;">
                        Validate check ins?
                        <form-tooltip
                            style="display:flex; align-items: center;"
                            tipwidth="100px"
                            leftPadding="40%"
                            message="Require users to verify check in using a confirmation code, by scanning a QR code, or by being within a certain distance of a location. If this is off, users will use the honor system."
                            >
                            <mwc-icon>info_outline</mwc-icon>
                        </form-tooltip>
                    </span>
            </div>
            ${this.should_verify_check_ins ? html`
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="allowUserEndDate"
                        name="allowUserEndDate"
                        class_name="blue"
                        ?checked="${this.enable_code_checkin}"
                        @change="${this.canCheckInWithCodeChange}"
                        >
                    </form-checkbox>
                    <span>Allow users to check in by entering a confirmation code provided at the location?</span>
                </div>
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="allowUserEndDate"
                        name="allowUserEndDate"
                        class_name="blue"
                        ?checked="${this.allow_qr_check_in}"
                        @change="${this.allowQRCheckInChange}"
                        >
                    </form-checkbox>
                    <span>Allow users to check in by scanning a QR code?</span>
                </div>
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo?.challengePassportEnabled}"
                        id="allowGeoLocationCheckIn"
                        name="allowGeoLocationCheckIn"
                        class_name="blue"
                        ?checked="${this.allow_geolocation_check_in}"
                        @change="${this.allowGeolocationCheckInChange}"
                        >
                    </form-checkbox>
                    <span>Allow users to check in by being within a certain distance of a location?</span>
                    ${this.allow_geolocation_check_in
                        ? html`
                            <proxi-textfield ?disabled="${!featuresInfo.challengePassportEnabled}"
                                outlined
                                class="input__challenge-title"
                                id="geofence-radius"
                                type="number"
                                label="Geofence Radius (in ${this.geofence_unit})"
                                placeholder="100"
                                .value="${round(this.translateRadiusFromMeters((this.default_geofence_radius != null && isNumber(this.default_geofence_radius)) ? this.default_geofence_radius : 20, this.geofence_unit), 2).toString()}"
                                @change="${this.changeDefaultGeofenceRadius}"
                            >
                            </proxi-textfield>
                            <proxi-selectfield-inline  id="unitSelect" outlined
                                    @change=${(e: CustomEvent) => {this.geofence_unit = (e.target as HTMLInputElement).value;}}
                                    value=${this.geofence_unit}>
                                <mwc-list-item value="meters" ?selected=${this.geofence_unit === 'meters'}>Meters</mwc-list-item>
                                <mwc-list-item value="miles" ?selected=${this.geofence_unit === 'miles'}>Miles</mwc-list-item>
                                <mwc-list-item value="feet" ?selected=${this.geofence_unit === 'feet'}>Feet</mwc-list-item>
                                <mwc-list-item value="kilometers" ?selected=${this.geofence_unit === 'kilometers'}>Kilometers</mwc-list-item>
                            </proxi-selectfield-inline>
                            `
                        : html``
                    }
                </div>
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="allowPhotoCheckIn"
                        name="allowPhotoCheckIn"
                        class_name="blue"
                        ?checked="${this.allow_photo_check_in}"
                        @change="${this.allowPhotoCheckInChange}"
                        >
                    </form-checkbox>
                    <span>Allow users to check in by submitting a photo?</span>
                </div>
                ${this.allow_photo_check_in ? html`
                    <div class="checkbox-wrapper">
                        <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                            id="allowPhotoCheckIn"
                            name="allowPhotoCheckIn"
                            class_name="blue"
                            ?checked="${this.auto_approve_photo_check_in}"
                            @change="${this.changePhotoAutoApprove}"
                            >
                        </form-checkbox>
                        <span>Automatically approve photo check ins?</span>
                    </div>
                ` : html``}
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="allowReceiptCheckIn"
                        name="allowReceiptCheckIn"
                        class_name="blue"
                        ?checked="${this.allow_receipt_check_in}"
                        @change="${this.allowReceiptCheckInChange}"
                        >
                    </form-checkbox>
                    <span>Allow users to check in by submitting receipt data?</span>
                </div>
                <h4 class="sub-title">Receipt Settings</h4>
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="autoApproveReceipt"
                        name="autoApproveReceipt"
                        class_name="blue"
                        ?checked="${this.auto_approve_receipt_check_in}"
                        @change="${this.changeReceiptAutoApprove}"
                        >
                    </form-checkbox>
                    <span>Automatically approve receipt check ins?</span>
                </div>
                <p class="subtext">Configure the currency and instructions for receipt submissions.</p>
                <proxi-select ?disabled="${!featuresInfo.challengePassportEnabled}"
                    outlined
                    class="input__challenge-title"
                    id="receipt-currency"
                    label="Currency"
                    .value="${this.receipt_currency}"
                    @change="${this.changeReceiptCurrency}"
                >
                    <option value="USD">USD - US Dollar</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="CNY">CNY - Chinese Yuan</option>
                    <option value="INR">INR - Indian Rupee</option>
                </proxi-select>
                <proxi-textarea ?disabled="${!featuresInfo.challengePassportEnabled}"
                    outlined
                    class="input__challenge-title"
                    id="receipt-instructions"
                    type="text"
                    label="Receipt Instructions"
                    placeholder="Instructions for submitting receipts"
                    .value="${this.receipt_instructions ?? ''}"
                    @input="${this.changeReceiptInstructions}"
                    >
                </proxi-textarea>
                <h4 class="sub-title">Special Offer Settings</h4>
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="allowUserEndDate"
                        name="allowUserEndDate"
                        class_name="blue"
                        ?checked="${this.should_show_special_offers}"
                        @change="${this.shouldShowSpecialOffersChange}"
                        >
                    </form-checkbox>
                    <span>Hide '${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Special Offer'}s' (if applicable) on map points until user check in?</span>
                </div>
                <h4 class="sub-title">Photo Instructions</h4>
                <p class="subtext">If users are having trouble checking in, we allow them to submit a photo for you to approve. Provide instructions for participants to follow when submitting photos for verification.</p>
                <proxi-textarea ?disabled="${!featuresInfo.challengePassportEnabled}"
                    outlined
                    class="input__challenge-title"
                    id="photo-instructions"
                    type="text"
                    label="Photo Instructions"
                    placeholder="Instructions for submitting photos"
                    value="${this.photo_instructions ?? ''}"
                    @change="${this.changePhotoInstructions}"
                >
                </proxi-textarea>
            ` : html`
                <p>Check ins are not required to be validated. Users can check in to any location at any time without any additional steps.</p>
            `}
        </settings-card>
        <settings-card header="Recurring Check Ins">
            <div class="header__actions" slot="header" style="display: flex; align-items: center;">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="allowRecurringCheckIns"
                        name="allowRecurringCheckIns"
                        class_name="blue"
                        ?checked="${isDefined(this.checkin_frequency_recurrance) && this.checkin_frequency_recurrance !== 'once'}"
                        @change="${this.changeMultipleCheckins}"
                        >
                    </form-checkbox>
                    <span style="display:flex; align-items: center; font-weight: normal;">
                        Allow Multiple Visits to Same Place?
                        <form-tooltip
                            style="display:flex; align-items: center;"
                            tipwidth="100px"
                            leftPadding="40%"
                            message="If enabled, users can earn points for checking in to the same location multiple times. If disabled, users can only earn points for checking in to a location once during the challenge."
                            >
                            <mwc-icon>info_outline</mwc-icon>
                        </form-tooltip>
                    </span>
            </div>
            ${this.checkin_frequency_recurrance && this.checkin_frequency_recurrance !== 'once' ? html`
                <p class="criteria"> Users will recieve points up for up to
                    ${this.checkin_frequency_quantity} ${this.checkin_frequency_quantity > 1 ? 'check ins' : 'check in'} every
                    ${this.checkin_frequency_recurrance === 'daily' ? 'day' : this.checkin_frequency_recurrance === 'weekly' ? 'week' : this.checkin_frequency_recurrance === 'monthly' ? 'month' : 'times'}.
                </p>

                <div class="success-criteria">
                    <span class="criteria_section">
                        <label>
                            <proxi-textfield
                                outlined
                                class="input reward-criteria-threshold"
                                id="reward_criteria"
                                type="number"
                                label=""
                                placeholder=""
                                value="${this.checkin_frequency_quantity ?? '1'}"
                                @change="${this.showCheckinFrequencyQuantityChange}"
                            >
                            </proxi-textfield>
                    </span>

                    <span class="criteria_section">
                        <proxi-selectfield
                            id="rewardCriteriaThreshold"
                            outlined
                            .height="${'1.5rem'}"
                            class="input reward-criteria"
                            @selected="${this.showCheckinFrequencyChange}"
                            value="${this.checkin_frequency_recurrance ?? 'once'}"
                        >
                            <mwc-list-item value="once">All Time</mwc-list-item>
                            <mwc-list-item value="daily">Daily</mwc-list-item>
                            <mwc-list-item value="weekly">Weekly</mwc-list-item>
                            <mwc-list-item value="monthly">Monthly</mwc-list-item>
                        </proxi-selectfield>
                    </span>

            ` : ''}
        </settings-card>
        <settings-card header="Awards and Badges">
            <p class="info-paragraph">These rewards are awarded to participants for earning a certain number of points or visiting a certain number of locations.</p>
            <challenge-rewards-wrapper ?disabled="${!featuresInfo.challengePassportEnabled}"
                class="awards-wrapper"
                @dataAction=${this.openChallengeReward}
                .data="${this.challengeRewards}"
                .activeData=${this.activeChallengeReward}
                .showNewReward=${!this.showChallengeRewardDrawer}
                @updateData=${this.updateReorderedRewardItems}
                @new-award=${this.createNewChallengeReward}
            ></challenge-rewards-wrapper>

            ${this.showChallengeRewardDrawer
                ? html`
                    <edit-challenge-rewards
                        .allData="${this.challengeRewards}"
                        .images="${this.imagesToUpload}"
                        .data="${this.activeChallengeReward}"
                        .activeIndex="${this.activeChallengeRewardIndex}"
                        @on-save-changes="${this.saveChallengeRewardsChanges}"
                        @closeDrawer="${this.closeChallengeReward}">
                    </edit-challenge-rewards>`
                : ''
            }
        </settings-card>
        `;
    }

    renderActivities() {
        return html`
            <settings-card header="Activity Points">
                <p class="info-paragraph">These points are awarded to participants for completing a challenge or task. They are not tied to any specific location.</p>
                <challenge-activity-wrapper ?disabled="${!featuresInfo.challengePassportEnabled}"
                    class="awards-wrapper"
                    @dataAction=${this.openChallengeActivity}
                    .data="${this.challengeActivities}"
                    .activeData=${this.activeChallengeActivity}
                    .showNewActivity=${!this.showChallengeActivityDrawer}
                    @updateData=${this.updateReorderedActivitysItems}
                    @new-activity=${this.createNewChallengeActivity}
                ></challenge-activity-wrapper>

                ${this.showChallengeActivityDrawer
                    ? html`
                        <edit-challenge-activities
                            .allData="${this.challengeActivities}"
                            .data="${this.activeChallengeActivity}"
                            .activeIndex="${this.activeChallengeActivityIndex}"
                            @on-save-changes="${this.saveChallengeActivityChanges}"
                            @closeDrawer="${this.closeChallengeActivity}">
                        </edit-challenge-activities>`
                    : ''
                }

            </settings-card>
        `;
    }

    renderServices() {
        const serviceConfig = {
            context: 'challenge' as const,
            setupPrice: '$250',
            contextName: 'challenge',
            contextDescription: 'challenges and maps'
        };

        return html`
            <professional-services-component
                .config=${serviceConfig}
                .amplitudeCallsite=${this.amplitude_callsite}>
            </professional-services-component>
        `;
    }

    async getRegistrationEmbedScript() {
        // Always use the keyed read link so the embed keeps working even if the
        // map is later flipped from searchable to unlisted.
        let link: string | undefined = undefinedIfEmpty(topicData.topic?.cc_read_link);
        if (link) {
            link = `${link}?view=registration&challenge_id=${this.challenge?._id?.$oid ?? ''}`;
            const code = `<iframe src="${link}" width="100%" height="100%" style="min-height:300px;overflow:hidden" frameborder="0" style="border:0" allowfullscreen></iframe>`;
            // copy to clipboard
            await navigator.clipboard.writeText(code);
            getMainPage().errorToastElement.showToast('Embed code copied to clipboard');
        } else {
            getMainPage().errorToastElement.showToastAndThrow('No registration form found');
        }
    }

    async getRegistrationLink() {
        const link = await challengeData.getRegistrationLink();
        await navigator.clipboard.writeText(link);
        getMainPage().errorToastElement.showToast('Registration link copied to clipboard');
    }

    async getRegistrationQrCode() {
        const { fileName, blobDataUrl } = await challengeData.getRegistrationQrCode();

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        a.href = blobDataUrl;
        a.download = fileName;
        a.click();
    }

    getChallengeLeaderboardLink() {
        const link = challengeData.getChallengeLeaderboardLink();
        navigator.clipboard.writeText(link).then(() => {
            getMainPage().errorToastElement.showToast('Leaderboard link copied to clipboard');
        }).catch((error) => {
            console.error('Error copying leaderboard link:', error);
            getMainPage().errorToastElement.showToast('Failed to copy link');
        });
    }

    getChallengePhotoFeedLink() {
        const link = challengeData.getChallengePhotoFeedLink();
        navigator.clipboard.writeText(link).then(() => {
            getMainPage().errorToastElement.showToast('Photo feed link copied to clipboard');
        }).catch((error) => {
            console.error('Error copying photo feed link:', error);
            getMainPage().errorToastElement.showToast('Failed to copy link');
        });
    }

    async printAllQrCodes() {
        // Get registration QR code.
        const topicQr = await challengeData.getRegistrationQrCode();

        // Download all QR codes.
        const locationQrCodes = await Promise.all(
            this.challengeLocations.filter(location => location.is_active).map(async (location) => {
                // Get the QR code.
                const { pointName, fileName, blobDataUrl } = await challengeData.downloadQrCodeForLocation(location._id?.$oid ?? '');

                // Return the QR code data.
                return {
                    pointName,
                    fileName,
                    blobDataUrl: blobDataUrl as string
                };
            })
        );

        // Get `img` tags for all QR codes.
        const locationQrCodeImageTags = locationQrCodes.map(qr => {
            // Return the HTML `img` tag.
            return `
                <div style="display: flex; flex-direction: column; align-items: center; margin: 20px;">
                    <h3><b>${topicQr.challengeName ?? ''}</b></h3>
                    <h3><b>${qr.pointName}</b> Check In</h3>
                    <img src="${qr.blobDataUrl ?? ''}" alt="${qr.fileName}" style="max-width: 100%; max-height: 100%;">
                    <div class="pagebreak"></div>
                </div>
            `;
        }).join('');

        // Create printable HTML.
        const htmlContent = `
            <html>
                <head>
                    <title>${this.challenge?.name ?? ''} QR Codes</title>
                    <style>
                        body {
                            font-family: ${topicData.topic?.custom_font ?? 'Montserrat'}, sans-serif;
                            padding: 20px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            flex-direction: column;
                        }

                        .pagebreak {
                            page-break-after: always;
                        }
                    </style>
                </head>
                <body>
                    ${locationQrCodeImageTags}
                </body>
            </html>
        `;

        // Create a hidden iframe.
        const iframe = document.createElement('iframe');
        document.body.appendChild(iframe);
        iframe.style.display = 'none';

        if (!isDefined(iframe.contentDocument) || !isDefined(iframe.contentWindow)) {
            getMainPage().errorToastElement.showToastAndThrow('Could not create iframe (due to JS limitations) for printing.');
            return;
        }

        // Write the content to the iframe.
        const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
        doc.open();
        doc.write(htmlContent);
        doc.close();

        // Wait for the content to load and then print
        iframe.onload = () => {
            // Print.
            iframe.contentWindow?.print();

            // Cleanup after print.
            window.onafterprint = event => {
                // Release the iframe.
                document.body.removeChild(iframe);

                // Release the blobs.
                locationQrCodes.forEach(qr => {
                    window.URL.revokeObjectURL(qr.blobDataUrl);
                });

                window.URL.revokeObjectURL(topicQr.blobDataUrl);
            };
        };
    }

    async getMapLink() {
        const link = topicData.topic?.cc_read_link;
        if (!link) {
            getMainPage().errorToastElement.showToastAndThrow('There was an error getting the map link');
            return;
        }
        await navigator.clipboard.writeText(link);
        getMainPage().errorToastElement.showToast('Map link copied to clipboard');
    }

    renderParticipatingPlaces() {
        return html`
        <assign-challenge-checkins-modal
            .data="${this.challengeLocations}"
            .challenge="${challengeData.challenge}"
            .codeRequired="${this.should_verify_check_ins}"
            @on-change-challenge-locations="${this.saveChallengeLocations}"
            @on-download-qr-codes="${this.printAllQrCodes}"
        ></assign-challenge-checkins-modal>
        `;
    }

    renderRegistrationOptions() {
        return html`
        <settings-card header="Share Treasure Hunt Form">
            ${this.challenge?.is_registration_active === true ? html`
                <p class="info-paragraph">Registration is active. Participants can register for the treasure hunt.</p>
                ` : html`
                <p class="info-paragraph">Registration is not active. Participants cannot register for the treasure hunt yet.</p>
                `}
            <br><p class="info-paragraph"> Users will first see the registration page, then be redirected to the treasure hunt event. If they have already registered, they will
                be redirected to the treasure hunt event directly or given the option to retrieve their participant link.
            </p>
            <div class="registration-options-container">
                <div class="registration-option" @click="${this.getRegistrationEmbedScript}">
                    <mwc-icon>code</mwc-icon>
                    <span>Get Embed Code</span>
                </div>
                <div class="registration-option" @click="${this.getRegistrationLink}">
                    <mwc-icon>content_copy</mwc-icon>
                    <span>Get Event Link</span>
                </div>
                <div class="registration-option" @click="${this.getRegistrationQrCode}">
                    <mwc-icon>qr_code_scanner</mwc-icon>
                    <span>Get Event QR Code</span>
                </div>
                ${this.challenge?.join_code ? html`
                    <div class="registration-option" @click="${this.getJoinCode}">
                        <mwc-icon>password</mwc-icon>
                        <span>Copy Join Code: ${this.challenge?.join_code}</span>
                    </div>
                ` :''}
            </div>
            ${this.challenge?.join_code ? html`
                <p class="info-paragraph">Users can join by going to ${window.location.hostname.toLowerCase() === 'betamap.proxi.co' ? 'betaplay.proxi.co' : 'play.proxi.co'} and entering the join code.</p>
            ` : ''}
            ${this.isEditingJoinCode ? html`
                <div>
                    <proxi-textfield
                        outlined
                        id="join-code"
                        charcounter
                        maxlength="6"
                        label="Join Code"
                        .value="${this.challenge?.join_code ?? ''}"
                    ></proxi-textfield>
                    <div class="form-buttons" style="display: flex; gap: 10px; flex-direction: row;">
                        <form-button size="sm" @click="${this.saveJoinCode}">Save</form-button>
                        <form-button variant="outline" size="sm" @click="${() => { this.isEditingJoinCode = false; }}">Cancel</form-button>
                    </div>
                </div>
            ` : html`
                <p class="info-paragraph">
                    <span @click="${() => { this.isEditingJoinCode = true; }}" class="link" style="cursor: pointer; text-decoration: underline;">
                        Customize Join Code</span>
                </p>
            `}
        </settings-card>
        <settings-card header="Registration Form Content">
            <proxi-textfield ?disabled="${!featuresInfo.challengePassportEnabled}"
                outlined
                class="input__challenge-title"
                id="registration-heading"
                type="text"
                label="Registration Form Heading"
                placeholder="Register for the Challenge"
                value="${this.registration_settings?.heading ?? ''}"
                @change="${this.onChangeRegistrationHeading}"
            >
            </proxi-textfield>
            <proxi-textarea ?disabled="${!featuresInfo.challengePassportEnabled}"
                outlined
                class="input__challenge-title"
                id="registration-description"
                type="text"
                label="Registration Form Description"
                placeholder="Enter your information to register for the challenge"
                value="${this.registration_settings?.description ?? ''}"
                @change="${this.onChangeRegistrationDescription}"
            >
            </proxi-textarea>
            <proxi-textfield ?disabled="${!featuresInfo.challengePassportEnabled}"
                outlined
                class="input__challenge-title"
                id="registration-button-text"
                type="text"
                label="Registration Button Text"
                placeholder="Register"
                value="${this.registration_settings?.button_label ?? ''}"
                @change="${this.onChangeRegistrationButtonText}"
            >
            </proxi-textfield>
            <div class="images-area" style="display: flex; flex-direction: row; gap: 10px; max-height: 300px; padding: 10px 0;">
                <div style="flex: 2;">
                    <p>Cover Image</p>
                    <div class="image-upload" style="display: flex; flex-direction: row; gap: 10px;">
                        ${this.registration_settings?.cover_image_url && this.imageEditing !== 'cover' ? html`
                            <div class="logo-container">
                                <img src="${this.registration_settings?.cover_image_url ?? ''}" alt="Current Cover" style="border-radius: 5px; padding: 5px; max-width: 100%; max-height: 100px;">
                                <mwc-icon @click="${() => {this.imageEditing = 'cover';}}" class="edit-icon">edit</mwc-icon>
                            </div>
                        ` : html`
                            <mwc-icon @click="${() => {this.imageEditing = undefined;}}" class="edit-icon">close</mwc-icon>
                            <image-upload-component
                                id="coverImageUpload"
                                style="flex: 2;"
                                .autoUpload="${true}"
                                @retreived-autoupload-url="${this.onChangeRegistrationCoverImage}"
                                @changed-image-url="${this.onChangeRegistrationCoverImage}"
                            ></image-upload-component>
                        `}
                    </div>
                    <p>Logo</p>
                    <div class="image-upload" style="display: flex; flex-direction: row; gap: 10px;">
                        ${this.registration_settings?.logo_url && this.imageEditing !== 'logo' ? html`
                            <div class="logo-container">
                                <img src="${this.registration_settings?.logo_url ?? ''}" alt="Current Logo" style="border-radius: 5px; padding: 5px; max-width: 100%; max-height: 100px;">
                                <mwc-icon @click="${() => {this.imageEditing = 'logo';}}" class="edit-icon">edit</mwc-icon>
                            </div>
                            <div class="checkbox-wrapper" style="align-items: center; margin-top: 10px;">
                                <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                                    id="showLogoBackground"
                                    name="showLogoBackground"
                                    class_name="blue"
                                    ?checked="${this.registration_settings?.show_logo_background ?? false}"
                                    @change="${this.onChangeRegistrationLogoBackground}"
                                    >
                                </form-checkbox>
                                <span>Show white background behind logo?</span>
                            </div>
                        ` : html`
                            ${this.registration_settings?.logo_url ? html`
                                <mwc-icon @click="${() => {this.imageEditing = undefined;}}" class="edit-icon">close</mwc-icon>
                            ` : ''}
                            <image-upload-component
                                id="coverImageUpload"
                                style="flex: 2;"
                                .autoUpload="${true}"
                                @retreived-autoupload-url="${this.onChangeRegistrationLogo}"
                                @changed-image-url="${this.onChangeRegistrationLogo}"
                            ></image-upload-component>
                        `}
                    </div>
                </div>
                <div style="flex: 1;" class="card-image">
                    <div class="card-image-holder card-cover-image">
                        <img src="${this.challenge?.registration_settings?.cover_image_url ?? ''}" alt="Challenge Cover Image">
                        ${this.challenge?.registration_settings?.logo_url ? html`
                            <div class="card-logo-overlay" style="${this.registration_settings?.show_logo_background ? 'background-color: white;' : 'background-color: transparent;box-shadow: none;'}">
                                <img src="${this.challenge?.registration_settings?.logo_url ?? ''}" alt="Challenge Logo">
                            </div>
                        ` : ''}
                    </div>
                </div>
            </div>
        </settings-card>
        <settings-card header="Form Fields">
            <div class="checkbox-wrapper">
                <span class="form-element-title">User Name</span>
                    <div class="form-checkbox-wrapper">
                        <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="requireUsername"
                        name="requireUsername"
                        class_name="blue"
                        ?checked="${this.registration_settings?.username_required}"
                        @change="${this.requireUsernameChange}"
                        ><span>Require</span>
                        </form-checkbox>
                   </div>
            </div>
            <div class="checkbox-wrapper">
                <span class="form-element-title">Email Address</span>
                <div class="form-checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="requireEmail"
                        name="requireEmail"
                        class_name="blue"
                        ?checked="${this.registration_settings?.email_required}"
                        @change="${this.requireEmailChange}"
                        ><span>Require</span>
                    </form-checkbox>
               </div>
            </div>
            <div class="checkbox-wrapper">
                <span class="form-element-title">Phone Number</span>
                <div class="form-checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="showPhone"
                        name="showPhone"
                        class_name="blue"
                        ?checked="${this.registration_settings?.show_phone}"
                        @change="${this.showPhoneChange}"
                        ><span>Show</span>
                    </form-checkbox>
                </div>
                <div class="form-checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                        id="requirePhone"
                        name="requirePhone"
                        class_name="blue"
                        ?checked="${this.registration_settings?.phone_required}"
                        @change="${this.requirePhoneChange}"
                        > <span>Require</span>
                    </form-checkbox>
                </div>
            </div>
            ${(this.registration_settings?.additional_fields?.length ?? 0) > 0 ? html`
                ${this.registration_settings?.additional_fields?.map((field, index) => html`
                    <br>
                    <span class="form-element-title">Custom Field ${index + 1}: ${field.label}</span>
                    <div class="custom-field-wrapper">
                        <proxi-textfield ?disabled="${!featuresInfo.challengePassportEnabled}"
                            outlined
                            class="input__challenge-title"
                            id="additionalField${index}"
                            type="text"
                            label="Field Name"
                            placeholder="Field Name"
                            value="${field.label}"
                            @change="${async (e: CustomEvent) => await this.changeAdditionalFields(index, 'label', e)}"
                        >
                        </proxi-textfield>
                        <div class="form-checkbox-wrapper">
                            <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                                id="showPhone"
                                name="showPhone"
                                class_name="blue"
                                label="Required"
                                ?checked="${field.required}"
                                @change="${async (e: CustomEvent) => await this.changeAdditionalFields(index, 'required', e)}"
                                > <span>Required</span>
                            </form-checkbox>
                        </div>
                        <proxi-selectfield-inline ?disabled="${!featuresInfo.challengePassportEnabled}"
                            id="unitSelect"
                            outlined
                            @selected=${async (e: CustomEvent) => await this.changeAdditionalFields(index, 'field_type', e)}
                            value=${field.field_type ?? 'text'}>
                            <mwc-list-item value="text">Text</mwc-list-item>
                            <mwc-list-item value="number">Number</mwc-list-item>
                            <mwc-list-item value="checkbox">Checkbox</mwc-list-item>
                            <mwc-list-item value="url" >URL</mwc-list-item>
                            <mwc-list-item value="select">Select</mwc-list-item>
                        </proxi-selectfield-inline>
                        <form-button size="xs" variant="outline" icon="delete" class="close-icon" style="cursor: pointer;" @click="${async () => await this.removeAdditionalField(index)}">
                        </form-button>
                    </div>
                    ${field.field_type === 'select' ? html`
                        <proxi-textfield ?disabled="${!featuresInfo.challengePassportEnabled}"
                            outlined
                            class="input__challenge-title"
                            id="additionalField${index}"
                            type="text"
                            label="Options (comma separated)"
                            placeholder="Option 1, Option 2, Option 3"
                            value="${field.options?.join(', ') ?? ''}"
                            @change="${async (e: CustomEvent) => await this.changeAdditionalFields(index, 'options', e)}"
                        >
                        </proxi-textfield>
                    ` : ''}
                    </div>
                `)}
            ` : ''}
            <proxi-textfield ?disabled="${!featuresInfo.challengePassportEnabled}"
                outlined
                class="input__challenge-title"
                id="challenge-title"
                type="text"
                label="Terms and Conditions URL (if applicable)"
                placeholder="URL"
                value="${this.challenge_terms ?? ''}"
                @change="${this.onChangeChallengeTerms}"
            >
            </proxi-textfield>
            <div class="form-checkbox-wrapper">
                <form-checkbox ?disabled="${!featuresInfo.challengePassportEnabled}"
                    id="termsRequired"
                    name="termsRequired"
                    class_name="blue"
                    ?checked="${this.challenge_terms_required}"
                    @change="${this.onChangeChallengeTermsRequired}"
                    > <span>Require Opt-in to Terms and Conditions to Register</span>
                </form-checkbox>
            </div>
            <form-button @click="${this.addAdditionalField}" size="sm" variant="outline" slot="header">
                Add Custom Field
            </form-button>
        </settings-card>
        `;
    }

    get requiredPaymentAmount() {
        const paymentDetails = this.challenge?.registration_settings?.payment_details;
        if (!isDefined(paymentDetails) || typeof paymentDetails !== 'object') {
            return 0;
        } else if ('Required' in paymentDetails) {
            return paymentDetails.Required.amount ?? 0; // Convert dollars to cents
        }
        return 0;
    }

    get suggestedDonationAmounts() : number[] {
        const paymentDetails = this.challenge?.registration_settings?.payment_details;
        if (!isDefined(paymentDetails) || typeof paymentDetails !== 'object') {
            return [];
        } else if ('Optional' in paymentDetails) {
            return paymentDetails.Optional.suggested_amounts?.map(amount => amount / 100) ?? []; // Convert dollars to cents
        }
        return [];
    }

    renderManageParticipantsAndResults() {
        return html`
            <div class="manage-participants-and-results">
                <div class="individual-participant-drawer ${isDefined(this.activatedChallenge) || this.showPendingApprovals ? 'open' : 'closed'}">
                    ${this.activatedChallenge ? html`
                        <mwc-icon class="close-icon" style="margin-bottom: 10px;float: right; position:fixed; z-index:4; cursor:pointer" @click="${this.handleCloseParticipantsProgressDetailsModal}">
                            close
                        </mwc-icon>
                        <challenge-participant-details-modal
                            style="height: 100%;"
                            .data=${this.activatedChallenge}
                            @on-save="${this.saveParticipantDetail}"
                            @refresh-challenges="${this.saveParticipantsProgress}"
                        ></challenge-participant-details-modal>
                    ` : ''}
                    ${this.showPendingApprovals ? html`
                        <mwc-icon class="close-icon" style="margin-bottom: 10px;float: right;" @click="${() => {this.showPendingApprovals = false;}}">
                            close
                        </mwc-icon>
                        <challenge-approvals-section
                            style="height: 100%;"
                            .data=${this.pendingPoints}
                            @updated-approvals="${this.saveParticipantsProgress}"
                        ></challenge-approvals-section>
                    ` : ''}
                </div>
                <div class="general-stats-area">
                    <div class="header-stats" @click="${this.getActivatedChallenges}" style="cursor: pointer; display: flex; align-items: center; --mdc-icon-size: 20px; justify-content: center; gap: 5px;">
                        <mwc-icon>refresh</mwc-icon>
                        Refresh
                    </div>
                    <div class="header-stats">
                        <settings-card header="Challenge Stats" .maxWidth=${'100%'}>
                            <div class="stats-holder">
                                <div class="stats-card third">
                                    <div class="stats-card__value">${this.activatedChallenges.length}</div>
                                    <div class="stats-card__label">People Registered</div>
                                </div>
                                <div class="stats-card third">
                                    <div class="stats-card__value">${this.total_checkin_count}</div>
                                    <div class="stats-card__label">Places Visited</div>
                                </div>

                                <div class="stats-card third">
                                    <div class="stats-card__value">${this.total_point_count}</div>
                                    <div class="stats-card__label">Points Earned</div>
                                </div>
                            </div>
                        </settings-card>
                        ${this.allImageUrls.length > 0 ? html`
                        <settings-card header="Challenge Images" .maxWidth=${'100%'} style="max-width: 300px;">
                            <div class="horizontal-image-holder-with-scroll">
                                <image-carousel
                                    borderRadius="4" .imageUrls="${this.allImageUrls}" .height="${'100%'}" .slideshow="${true}">
                                </image-carousel>
                            </div>
                            <form-button @click="${() => { this.getChallengePhotoFeedLink(); }}" style="margin-top: 10px;" size="sm" variant="outline">
                                Copy Standalone Feed Link
                            </form-button>
                        </settings-card>
                        ` : ''}
                        <settings-card header="Awards Earned" .maxWidth=${'100%'}>
                            <p class="info-paragraph">Click on an award to filter participants by that award.</p>
                            ${this.usersAreFiltered ? html`
                                <form-button @click="${this.clearFilterUsers}" style="margin-bottom: 10px;" size="sm" variant="outline" slot="header">
                                    Clear Rewards Filter
                                </form-button>
                            ` : ''}
                            ${this.earned_rewards.length > 0
                                ? html`
                                    <div class="awards__wrapper">
                                        ${this.earned_rewards.map((reward, index) => html`
                                            <div class="awards__card" @click="${async () => await this.filterUsersbyReward(reward.reward_id)}" style="cursor: pointer;">
                                                <span class="award-count">${reward.count}</span>
                                                <img
                                                    src="${reward.image_url}"
                                                    class="award-icon"
                                                    alt="${reward.title}"
                                                />
                                                <div class="awards__label">${reward.title}</div>
                                            </div>
                                        `)}
                                    </div>
                                `
                                : html`
                                    <p>No awards have been earned yet.</p>
                                `
                            }
                        </settings-card>
                        ${!this.showPendingApprovals && this.pendingPoints.length > 0 ? html`
                            <settings-card header="Points Pending Approval" .maxWidth=${'100%'}>
                                <div style="display: flex; flex-direction: column; gap: 10px;">
                                    You have ${this.pendingPoints.length} pending points to approve.
                                    <form-button
                                        @click="${this.viewPendingCheckinList}"
                                    >
                                        Review Now
                                    </form-button>
                                </div>
                            </settings-card>
                        ` : ''}
                    </div>
                    <challenge-participants-progress-modal
                        style="flex:1;overflow:hidden;"
                        .data=${this.activatedChallenges}
                        .challenge_data=${this.challenge}
                        @on-save="${this.saveParticipantsProgress}"
                        @on-view-details="${this.viewParticipantsProgressDetails}"
                        @on-copy-embeddable-link="${this.getChallengeLeaderboardLink}"
                        @refresh-challenges="${this.getActivatedChallenges}"
                    ></challenge-participants-progress-modal>
                </div>
            </div>
        `;
    }

    renderCheckIns() {
        return html`
        <challenge-locations-progress .data="${this.challengeLocations}"> </challenge-locations-progress>
        `;
    }

    renderMessageCenter() {
        return html`
            <settings-card header="Message Participants (Beta)">
                <p class="info-paragraph">Send a custom message to all participants. The email will include a link to their individual page.</p>
                    <create-message-component
                    id="createMessageComponent"
                        .message_target_type=${EventTargetType.ActivatedChallenge}
                        .message_target_id=${this.challenge?._id?.$oid}
                        .featureEnabled=${featuresInfo.challengePassportEnabled}
                        @message-sent="${this.getChallengeMessages}"
                    >
                    </create-message-component>
                </settings-card>
                <settings-card header="Sent and Scheduled Messages">
                    <p class="info-paragraph">View and manage messages sent to participants.</p>
                    ${this.challengeMessages.length > 0 ? html`
                        <past-scheduled-message-component
                            .data="${this.challengeMessages}"
                            .message_target_type=${EventTargetType.ActivatedChallenge}
                            @set-edited-message="${this.setEditedMessage}"
                        >
                        </past-scheduled-message-component>
                    ` : html`
                        <p>No messages sent yet.</p>
                    `}
            </settings-card>
        `;
    }

    renderPreCreationView() {
        return html`
        <div class="pre-creation-view" style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2rem;">
            <h2>Create a New Treasure Hunt</h2>
            <p class="info-paragraph">Create a new treasure hunt from scratch or import an existing treasure hunt from another project.</p>
            <div class="pre-creation-options">
                <div>
                    <h3>Start Fresh</h3>
                    <p class="info-paragraph">Create a brand new treasure hunt and customize it to fit your needs.</p>
                    <form-button size="md" @click="${this.createNewChallenge}">Create New Treasure Hunt</form-button>
                </div>
                <div>
                    <h3>Import Existing Treasure Hunt</h3>
                    <p class="info-paragraph">Import a treasure hunt from another project to save time and effort.</p>
                    <proxi-selectfield id="import-challenge-select" outlined
                        @selected=${(e: CustomEvent) => {this.import_challenge_id = (e.target as HTMLInputElement).value;}}>
                        <mwc-list-item value="" ?selected=${this.import_challenge_id === ''}>Select an Existing Treasure Hunt to Copy</mwc-list-item>
                        ${this.importableChallenges.map((challenge) => html`
                            <mwc-list-item value="${challenge._id?.$oid ?? ''}" ?selected=${this.import_challenge_id === (challenge._id?.$oid ?? '')}>
                                ${challenge.name}
                            </mwc-list-item>
                        `)}
                    </proxi-selectfield>
                    <form-button size="md" ?disabled="${!this.import_challenge_id}" @click="${this.importChallenge}">Copy Treasure Hunt</form-button>
                </div>
            </div>
        </div>
        `;
    }

    async importChallenge() {
        if (!this.import_challenge_id) {
            getMainPage().errorToastElement.showToastAndThrow('Please select a treasure hunt to import.');
            return;
        }
        try {
            await duplicateChallengeToTopic(this.import_challenge_id, topicData.topicId);
            getMainPage().errorToastElement.showToast('Treasure Hunt imported successfully.');
            await topicData.getNewChallenges();
            this.preCreationView = false;
            await this.startup();
        } catch (error) {
            console.error('Error importing treasure hunt:', error);
            getMainPage().errorToastElement.showToastAndThrow('There was an error importing the treasure hunt. Please try again later.');
        }
    }

    static get styles() {
        return css`
            .wrapper {
                display: flex;
                flex-direction: column;
                overflow: hidden auto;
                box-sizing: border-box;
                width: 100%;
                height: 100%
            }

            .header-title-and-buttons {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                padding: 10px;
                font-size: 18px;
                background-color: var(--theme-border-color);
                border-bottom: 1px solid var(--theme-border-color);
            }

            .left-header-content {
                display: flex;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap: 5px;
                flex: 1;
            }

            .right-header-content {
                display: flex;
                flex-wrap: wrap;
                flex-direction: row;
                align-items:center;
                font-size: 0.8rem;
                gap:5px;
                justify-content: flex-end;
                flex: 1;
            }

            .middle-header-content {
                flex: 1;
                font-size: 1rem;
                text-align:center;
            }

            .header-title-and-buttons h4 {
                margin: 5px 0;
            }

            .tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 20px;
                background-color: var(--theme-white-color);
                border-bottom: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
            }

            .tab__content {
                padding: 20px;
                display: flex;
                gap: 20px;
                flex: 1;
                overflow: hidden auto;
                justify-content: flex-start;
                align-items: stretch;
                background-color: var(--theme-border-color);
                flex-direction: column;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .manage-participants-and-results {
                display: flex;
                flex-direction: row;
                gap: 10px;
                height: 100%;
            }

            .individual-participant-drawer {
                border-right: 1px solid var(--theme-border-color);
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
                background-color: var(--theme-white-color);
                padding: 1rem;
                transition: width 0.3s;
                display: flex;
                flex-direction: column;
            }

            .individual-participant-drawer.open {
                width: 30%;
            }

            .individual-participant-drawer.closed {
                width: 0;
                padding: 0;
            }

            .form-checkbox-wrapper {
                display: flex;
                flex-direction: row;
                gap: 5px;
                align-items: center;
            }

            .custom-field-wrapper {
                display: flex;
                flex-direction: row;
                gap: 10px;
                align-items: center;
                white-space: nowrap;
            }

            .form-element-title {
                font-weight: 700;
                color: #000000;
                text-align: left;
            }

            .stats-holder {
                display: flex;
                flex-direction: row;
            }

            .general-stats-area {
                display: flex;
                flex-direction: column;
                gap: 10px;
                flex: 1;
            }

            .header-stats {
                display: flex;
                flex-direction: row;
                gap: 10px;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 5px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            h3.sub-title {
                font-weight: 700;
                font-size: 16px;
                line-height: 150%;
                color: #000000;
                text-align: left;
                margin: 15px 0 5px;
            }

            .challenge-details__tabs {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 20px;
                border: 1px solid var(--main-primary-color);
            }

            .challenge-details__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                line-height: 150%;
                color: var(--main-primary-color);
                background: transparent;
                padding: 5px 10px;
                cursor: pointer;
            }

            .challenge-details__tab.right {
                border-radius: 0px 20px 20px 0px;
            }

            .challenge-details__tab.left {
                border-radius: 20px 0px 0px 20px;
            }

            .challenge-details__tab.active {
                background: var(--main-primary-color);
                color: var(--theme-white-color);
            }

            .chackboxHolder {
                display:flex;
                flex-direction:row;
                gap: 5px;
                align-items:center;
            }

            .tab-icon {
                width: 12px;
                height: 12px;
                margin-right: 5px;
            }

            .tab-icon.small {
                width: 12px;
                height: 12px;
            }

            .input__challenge-title {
                width: 100%;
                margin-top: 15px;
            }

            p {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .registration-options-container {
                display: flex;
                flex-direction: row;
                justify-content: space-around;
                margin: 16px 0;
                gap: 10px;
            }

            .registration-option {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 16px;
                border: 1px solid var(--theme-border-color);
                border-radius: 8px;
                cursor: pointer;
                flex: 1;
                background-color: var(--theme-white-color);
                transition: background-color 0.2s ease;
            }

            .registration-option:hover {
                background-color: var(--theme-border-color);
            }

            .registration-option mwc-icon {
                font-size: 32px;
                margin-bottom: 8px;
                color: var(--main-primary-color);
            }

            .registration-option span {
                text-align: center;
                font-size: 14px;
            }

            .addon__footer {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                flex-wrap: wrap;
                align-items: center;
                padding: 0 20px;
                gap: 10px;
            }

            .challeng-save-button {
                --mdc-theme-primary: var(--main-success-color);
                --mdc-typography-button-text-transform: case;
                margin: 10px 0;
            }

            .date-container {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                margin-top: 15px;
            }

            .input__date {
                width: 48%;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
                margin-top: 15px;
            }

            #allowUserEndDate,
            #showLeaderboard {
                --form-checkbox-label-color: var(--theme-input-color);
            }

            .edit__textfield {
                width: 100%;
                margin-top: 15px;
            }

            .awards-wrapper {
                margin: 20px 0;
            }

            .button__wrapper {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                margin: 15px 0;
            }

            .hidden {
                display: none;
            }

            .assign-checkin-code-button,
            .view_participants_progress {
                width: 100%;
                line-height: 15px;
                border-radius: 8px;
                margin: 15px 0;
                border: 1px solid var(--main-primary-color);
                --mdc-theme-on-primary: var(--main-primary-color);
                --mdc-theme-primary: transparent;
                --mdc-button-height: 50px;
                --mdc-typography-button-font-size: 12px;
                --mdc-typography-button-text-transform: capitalize;
            }

            .not-full-width {
                width: unset;
            }

            .redButton {
                --mdc-theme-primary: var(--main-invalid-color);
            }


            .redText {
                color: var(--main-invalid-color);
            }

            .challenge-save-button {
                font-size: 16px;
                border-radius: 8px;
                --mdc-theme-on-primary: var(--theme-white-color);
                --mdc-typography-button-text-transform: capitalize;
                --mdc-theme-primary: #5EA667;
            }

            .image-upload {
                display: flex;
                flex-direction: row;
                gap: 10px;
            }

            .greyed-out {
                --mdc-theme-primary: grey;
            }

            .horizontal-image-holder-with-scroll {
                overflow-x: auto;
                white-space: nowrap;
                padding: 10px;
                max-height: 100px;
            }

            .image-card {
                display: inline-block;
                height: 100px;
                width: 100px;
                background-size: cover;
                background-position: center;
                margin-right: 10px;
                flex: none;
                border-radius: 5px;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }

            .image-card:last-child {
                margin-right: 0;
            }

            .stats-card-container {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                box-sizing: border-box;
            }

            .download__options {
                width: 100%;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                gap: 10px;
            }

            .stats-card {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                border: 1px solid #cac4d0;
                border-radius: 12px;
                box-sizing: border-box;
                text-align: center;
                font-size: 0.8rem;
                padding: 0.25rem;
            }

            .stats-card.third {
                width: calc(33.33% - 12px);
                margin: 15px 5px;
            }

            .stats-card__value {
                font-size: 24px;
                line-height: 130%;
                color: #000000;
                font-weight: 700;
                maring-right: 4px;
            }

            .stats-card__label {
                line-height: 125%;
                color: #1d1b20;
            }

            .awards-earned {
                width: 100%;
                text-align: center;
                font-weight: 700;
                font-size: 16px;
                line-height: 150%;
                color: #000000;
                margin: 15px 0;
            }

            .awards__wrapper {
                max-width: 30vw;
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                overflow-x: auto;
            }

            .awards__card {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0px 5px;
                flex: 1;
            }

            .award-icon {
                width: 20px;
                height: 20px;
            }

            .award-count {
                font-weight: 700;
                font-size: 18px;
                line-height: 130%;
                color: #000000;
                text-align: center;
            }

            .awards__label {
                text-align: center;
                font-size: 10px;
            }

                        .card-image {
                width: 50%;
                height: auto;
                overflow: hidden;

                display: flex;
                align-items: center;
                justify-content: center;
                border-bottom-right-radius: 10px;
                border-top-right-radius: 10px;
                padding-right: 10px;
            }

            .card-image-holder {
                position: relative; /* Make it a positioning context */
                overflow: hidden;
                min-height: 100%;
                max-height: 100%;
                display: flex;
                border-radius: 10px;
            }

            .card-cover-image img {
                overflow: hidden;
                object-fit: cover;
                min-height: 100%;
                min-width: 100%;
            }

            .card-logo-overlay {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                display: flex;
                justify-content: center;
                align-items: center;
                max-width: 80%;
                max-height: 80%;
                padding: 5px;
                border-radius:6px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                z-index: 2;
            }

            .card-logo-overlay img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
            }

            .card-logo-only {
                display: flex;
                justify-content: center;
                align-items: center;
            }

            .card-logo-only img {
                max-width: 80%;
                max-height: 80%;
                object-fit: contain;
                background-color: white;
                border-radius: 10px;
                padding: 10px;
            }

            #assignChallengeCheckinsDrawer,
            #challengeParticipantsProgressDrawer,
            #challengeParticipantDetailsDrawer {
                --bottom-height: 0px;
                --bottom-height-half: 0px;
            }
        `;
    }
}

